import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { Crosshair, Layers2, MapPinPlus, Minus, MountainSnow, Plus, X } from "lucide-react";
import { forward as toMgrs } from "mgrs";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { api, dossierParam, mapRequest } from "../api/client";
import type { GraphicFamily, LayerKey, MeasureKey, Phase } from "../api/types";
import { isCompany } from "../company";
import { NoticeToast } from "../components/NoticeToast";
import { Motion, Presence, useLastDefined } from "../components/presence";
import { Button, IconButton } from "../components/ui";
import { config } from "../config";
import { t, tServer } from "../i18n/i18n";
import { type Draft, type LinePreview, useUi } from "../state/ui";
import { bridgeImageExpression } from "./bridgeImages";
import { BRIDGE_LAYER_ID, INTERACTIVE_LAYER_IDS, LAYER_ORDER, LAYER_STYLES, layerKeyOf } from "./layerStyles";
import { revealPaint, useMapRevealed } from "./reveal";
import { useBridgeState } from "./useBridgeState";
import { moveM, pointAlongPolyline } from "./geo";
import { CorridorLayers, LINE_HIT_LAYER_ID, lineGeometry, slideMeasure } from "./units/CorridorLayers";
import { DraftBanner } from "./graphics/DraftBanner";
import { DraftLayers, GRAPHIC_EDGE_LAYER_ID, GRAPHIC_FILL_LAYER_ID, GRAPHIC_LAYER_IDS, GraphicsLayers } from "./graphics/GraphicsLayers";
import { isSelfIntersecting } from "./graphics/geometry";
import { findGraphic, MAX_LINE_POINTS, useGraphics } from "./graphics/useGraphics";
import { ANNOTATION_LAYER_ID, AnnotationDraftLayer, AnnotationsLayers } from "./annotations/AnnotationsLayers";
import { useAnnotationMenu } from "./annotations/AnnotationMenu";
import {
  type AnnotationMenuBlock,
  annotationBlockNotice,
  annotationButtonTitle,
  annotationMenuBlock,
  ANNOTATION_TIP_DELAY_MS,
  MAP_COVERING_VIEWS,
  useAnnotationTip,
} from "./annotations/annotationAccess";
import { AnnotationTip } from "./annotations/AnnotationTip";
import { createLongPress, LONG_PRESS_MS, MOUSE_SLOP_PX, TOUCH_SLOP_PX } from "./longPress";
import { useAnnotations } from "./annotations/useAnnotations";
import { TerrainLayers } from "./terrain/TerrainLayers";
import { PhaseBar } from "./units/PhaseBar";
import { provideImage } from "./images";
import { useLineLabel } from "./lineNames";
import { CompanyLayers } from "./units/CompanyLayers";
import { loadMissionImages } from "./units/missionImages";
import { MissionSymbolsLayers } from "./units/MissionSymbolsLayers";
import { PropagationCard } from "./units/PropagationCard";
import { PropagationLayers } from "./units/PropagationLayers";
import { AssistantRouteLayers } from "./roads/AssistantRouteLayers";
import { TacticalRoadsLayers } from "./roads/TacticalRoadsLayers";
import { CountryBordersLayers } from "./country/CountryBordersLayers";
import { FireZonesLayers } from "./fires/FireZonesLayers";
import { DronesLayers } from "./drones/DronesLayers";
import { VisibilityLayers } from "./visibility/VisibilityLayers";
import { SUBUNIT_LAYER_ID, UNIT_LAYER_ID, UnitsLayers } from "./units/UnitsLayers";
import { useZoomLevelsDriver, type ZoomMap } from "./units/zoomLevels";
import { UnitHoverOverlay, useUnitHover } from "./units/UnitHover";
import { registerMapDrop } from "./units/listDrag";
import { useDeployment } from "./units/useDeployment";

import { usePlaceFraming } from "./placeFraming";
import { entryZoom, fittingZoom, unitsCentroid } from "./recenter";
import { useUnitMoves } from "./units/useUnitMoves";
import { useUnitMenu } from "./units/useUnitMenu";
import { useHistoryKeys } from "./useHistoryKeys";
import {
  type Band,
  bannerPlace,
  type BannerPlace,
  bottomBandFor,
  MGRS_FULL_PX,
  MGRS_TIGHT_PX,
  noticePlace,
  phaseTitleFits,
  type Rect,
  STATUS_ALL,
  STATUS_GAP_PX,
  settleStatusFit,
  statusFit,
  type StatusFit,
  topBannerFor,
} from "./manoeuvre/freeBands";
import { HistoryButtons, ManoeuvreBanner, OrientationBanner } from "./manoeuvre/ManoeuvreBanner";
import { prefersReducedMotion } from "./manoeuvre/motion";
import { OrientationOverlay } from "./manoeuvre/OrientationOverlay";
import { useOrientation } from "./manoeuvre/useOrientation";
import { ManoeuvreLayers } from "./manoeuvre/ManoeuvreLayers";
import { MeasureNamesToggle } from "./manoeuvre/MeasureNamesToggle";
import { measureLevelOf } from "./manoeuvre/measureView";
import { type MeasureSession, useMeasureGestures } from "./manoeuvre/useMeasureGestures";
import { MEASURE_HIT_LAYER_ID, ZoMeasuresLayers } from "./manoeuvre/ZoMeasuresLayers";
import { pickTarget, topFeature } from "./manoeuvre/pick";
import { type ManoeuvreHandle, useManoeuvre } from "./manoeuvre/useManoeuvre";
import { flashLineTag, type GestureSession, type Modifiers, useManoeuvreGestures } from "./manoeuvre/useManoeuvreGestures";
import { designate, designateSoon } from "../components/attention";
import { effectiveBasemap as basemapShown, forcedBasemap } from "./zones/basemap";
import { useOpenZone } from "./zones/useZones";
import { extendZoneAction } from "./zones/zoneAttention";
import { clickZoneDraw, useZoneDraw } from "./zones/zoneDraw";
import { ZoneDrawBanner } from "./zones/ZoneDrawBanner";
import { ZoneDrawLayers } from "./zones/ZoneDrawLayers";
import { refuseIfDossierLocked, useDossierLocked } from "../sharing/access";
import { useHelp } from "../help/helpStore";
import { type MapSource, setCaptureMap } from "../feedback/capture";

/** Zoom à partir duquel les ponts d'un terrain de corps d'armée se montrent (docs/08 §23). */
const CORPS_BRIDGE_MINZOOM = 9;

/** Moteur chargé, avec le contrôle d'attribution de la même famille (react-map-gl) : l'attribution est posée
 *  repliée (« i »), pour que les barres basses tiennent sur une ligne au centre sans la recouvrir (R7). */
type EngineModule = typeof import("./engines/mapbox") & {
  AttributionControl: typeof import("react-map-gl/mapbox").AttributionControl;
};

/** Distance en mètres entre deux points [lon, lat] (haversine). */
function metersBetween(a: [number, number], b: [number, number]): number {
  const rad = Math.PI / 180;
  const dLat = (b[1] - a[1]) * rad;
  const dLon = (b[0] - a[0]) * rad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a[1] * rad) * Math.cos(b[1] * rad) * Math.sin(dLon / 2) ** 2;
  return 6_371_000 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

/** Événement pointeur de la carte (souris ou tactile), dans le sous-ensemble commun aux deux moteurs. */
interface PointerEvt {
  point: { x: number; y: number };
  lngLat: { lng: number; lat: number };
  features?: { layer?: { id?: string }; properties?: Record<string, unknown> | null; geometry?: unknown }[];
  preventDefault: () => void;
}

/** Sous-ensemble de l'API de carte commun à Mapbox GL JS et MapLibre GL JS, pour les symboles. */
interface SymbolHost {
  on(type: "styleimagemissing", listener: (ev: { id: string }) => void): unknown;
  hasImage(id: string): boolean;
  addImage(id: string, image: ImageData, options?: { pixelRatio?: number }): void;
}

/** Cartes déjà à l'écoute des images manquantes. */
const imageHosts = new WeakSet<object>();

/** Symboles APP-6D, pont OTAN et motifs du terrain fournis à la demande dès la création de la carte : la
 *  première mise en page des couches précède l'événement « load », et une image demandée avant l'écouteur
 *  restait manquante (unités et ponts invisibles quand le dossier était déjà en cache). */
function provideMissingImages(map: SymbolHost) {
  if (imageHosts.has(map)) return;
  imageHosts.add(map);
  map.on("styleimagemissing", (ev: { id: string }) => provideImage(map, ev.id));
}

/** Objet retenu sous le pointeur : priorités hors édition et en mode édition du fuseau dans manoeuvre/pick.ts
 *  (unité, ligne de coordination, pont, point de passage, graphique… ; en édition, les poignées d'abord). */

/** Deux touchers sur la même poignée à moins de 350 ms : retrait (le double-clic de la souris). */
const DOUBLE_TAP_MS = 350;

/** Touches de modification d'un événement de la carte (souris ; aucune au doigt). */
function modifiersOf(e: unknown): Modifiers {
  const original = (e as { originalEvent?: { altKey?: boolean; shiftKey?: boolean } }).originalEvent;
  return { alt: Boolean(original?.altKey), shift: Boolean(original?.shiftKey) };
}

/** Nombre de doigts posés (événement tactile de la carte). */
function touchCount(e: unknown): number {
  const original = (e as { originalEvent?: { touches?: { length: number } } }).originalEvent;
  return original?.touches?.length ?? 1;
}

/** Position du pointeur dans la fenêtre (menu de l'unité). */
function clientPoint(e: unknown): { x: number; y: number } | null {
  const original = (e as { originalEvent?: MouseEvent | TouchEvent }).originalEvent;
  if (!original) return null;
  if ("touches" in original) {
    const touch = original.touches[0] ?? original.changedTouches[0];
    return touch ? { x: touch.clientX, y: touch.clientY } : null;
  }
  return { x: original.clientX, y: original.clientY };
}

const isGraphicLayer = (id: string | undefined) => id === GRAPHIC_EDGE_LAYER_ID || id === GRAPHIC_FILL_LAYER_ID;

/** Tracé point par point (ligne, zone d'analyse du terrain), par opposition au graphique posé d'un clic. */
const isMultiPoint = (draft: Draft) => draft.family === "lineGraphics" || draft.purpose === "terrain";

const FRANCE_VIEW = { longitude: 2.6, latitude: 46.6, zoom: 5.2 };
/** Voile d'éclaircissement du fond de carte. */
const VEIL_ID = "basemap-veil";
// Emprise du camp de Mailly (SW 48,5720 / 4,2278, NE 48,7152 / 4,4062) resserrée de 8 % par côté.
const CENTAC_FRAME = [4.24207, 48.58346, 4.39193, 48.70374] as const;

/** Coordonnée MGRS lisible (« 31U EQ 12345 67890 »), comme le relevé sous le curseur de Prevail ; à 100 m
 *  (`digits` = 3 : « 31U EQ 967 330 »), comme les références citées par l'ordre. */
export function formatMgrs(lng: number, lat: number, digits: 3 | 5 = 5): string {
  try {
    const raw = toMgrs([lng, lat], digits);
    const match = new RegExp(`^(\\d{1,2}[C-X])([A-Z]{2})(\\d{${digits}})(\\d{${digits}})$`).exec(raw);
    return match ? `${match[1]} ${match[2]} ${match[3]} ${match[4]}` : raw;
  } catch {
    return "—";
  }
}

function maplibreBaseStyle() {
  return {
    version: 8 as const,
    glyphs: config.maplibreGlyphs,
    sources: {},
    // Fond du style « plan IGN » de l'iPad (style-plan-ign.json).
    layers: [{ id: "background", type: "background" as const, paint: { "background-color": "#e8e8e8" } }],
  };
}

/** Marges de cadrage autour des panneaux flottants (vue, calques, fiche, assistant) : la bande libre entre les
 *  panneaux de gauche et de droite, ou, si elle est trop étroite (fenêtre étroite), le bas de la carte, sous eux.
 *  Mapbox GL refuse de cadrer si les marges dépassent la carte : elles sont plafonnées. */
function freePadding(container: HTMLElement): { top: number; bottom: number; left: number; right: number } {
  const box = container.getBoundingClientRect();
  const panels = [...document.querySelectorAll<HTMLElement>("[data-floating-panel]")]
    .map((p) => p.getBoundingClientRect())
    .filter((r) => r.width > 0 && r.height > 0 && r.right > box.left && r.left < box.right);
  const bottom = 90; // barre des temps et du zoom
  let left = 24;
  let right = 24;
  let top = 60;
  for (const r of panels) {
    // Barre courte collée en haut (calques repliés) : elle ne masque que le haut de la carte.
    if (r.top - box.top < 80 && r.height < box.height * 0.25) top = Math.max(top, r.bottom - box.top + 16);
    else if ((r.left + r.right) / 2 - box.left < box.width / 2) left = Math.max(left, r.right - box.left + 24);
    else right = Math.max(right, box.right - r.left + 24);
  }
  if (box.width - left - right >= 220) return { top, bottom, left, right };
  const under = Math.max(60, ...panels.map((r) => r.bottom - box.top + 24));
  return { top: Math.min(under, Math.max(60, box.height - bottom - 160)), bottom, left: 24, right: 24 };
}

/** Rectangles des panneaux flottants et de la carte (pixels de la page) : entrée des bandes libres (`freeBands`). */
function measured(container: HTMLElement): { box: Rect; panels: Rect[] } {
  const box = container.getBoundingClientRect();
  const panels = [...document.querySelectorAll<HTMLElement>("[data-floating-panel]")].map((p) => p.getBoundingClientRect());
  return { box, panels };
}

/** Place des bandeaux du haut de la carte (`bannerPlace`) : jamais sur un panneau. */
function freeTopBand(container: HTMLElement): BannerPlace | null {
  const { box, panels } = measured(container);
  return bannerPlace(box, panels);
}

/** Pointeur grossier (doigt) au moment de l'appel : la consigne parle de toucher, pas de clic. */
function coarsePointer(): boolean {
  try {
    return window.matchMedia("(pointer: coarse)").matches;
  } catch {
    return false;
  }
}

/** Coins bas du fond de carte (échelle et logo à gauche, attribution à droite), Mapbox comme MapLibre : les barres
 *  basses et l'avis ne les recouvrent jamais (R7). Un coin vide mesure 0 et ne compte pas. */
const MAP_CORNERS =
  ".mapboxgl-ctrl-bottom-left, .mapboxgl-ctrl-bottom-right, .maplibregl-ctrl-bottom-left, .maplibregl-ctrl-bottom-right";
function mapCorners(container: HTMLElement): HTMLElement[] {
  return [...container.querySelectorAll<HTMLElement>(MAP_CORNERS)];
}

/** Bande basse : rangée à côté des panneaux ou entre les coins du fond de carte quand, centrées, les barres les
 *  croiseraient (`bottomBandFor`). */
function freeBottomBand(container: HTMLElement, height: number, width: number): Band | null {
  const { box, panels } = measured(container);
  const corners = mapCorners(container).map((c) => c.getBoundingClientRect());
  return bottomBandFor(box, panels, height, corners, width);
}

/** Place de l'avis au-dessus des barres basses (`noticePlace`), jamais sur une bulle de prise en main
 *  (`[data-notice-avoid]`). */
function freeNoticePlace(container: HTMLElement, barsHeight: number) {
  const { box, panels } = measured(container);
  const corners = mapCorners(container).map((c) => c.getBoundingClientRect());
  const avoided = [...document.querySelectorAll<HTMLElement>("[data-notice-avoid]")].map((b) => b.getBoundingClientRect());
  return noticePlace(box, panels, barsHeight, corners, avoided);
}

export function MapCanvas() {
  const [engine, setEngine] = useState<EngineModule | null>(null);
  const [cursor, setCursor] = useState<string>("—");
  // Carte chargée : ses coins (échelle, logo, attribution) existent, la bande basse peut les mesurer.
  const [mapLoaded, setMapLoaded] = useState(false);
  const [zoom, setZoom] = useState(FRANCE_VIEW.zoom);
  // Cap de la carte (rotation au doigt seulement), arrondi à 5° : place des étiquettes de LIMA.
  const [bearing, setBearing] = useState(0);
  const [hovering, setHovering] = useState(false);
  const mapRef = useRef<import("react-map-gl/mapbox").MapRef | null>(null);

  const mapSlug = useUi((s) => s.mapSlug);
  const visible = useUi((s) => s.visible);
  const freeTerrainRivers = useUi((s) => s.freeTerrainRivers);
  const basemap = useUi((s) => s.basemap);
  const mapLighten = useUi((s) => s.mapLighten);
  const select = useUi((s) => s.select);
  const flyTo = useUi((s) => s.flyTo);
  const phase = useUi((s) => s.phase);
  const setDragPreview = useUi((s) => s.setDragPreview);
  const notify = useUi((s) => s.notify);
  const requestSave = useUi((s) => s.requestSave);
  const exerciseId = useUi((s) => s.exerciseId);
  const lineLabel = useLineLabel();
  const { canMove, move, moveLine } = useUnitMoves();
  const setLinePreview = useUi((s) => s.setLinePreview);
  const { data: deployment, isPlaceholderData: deploymentPending } = useDeployment();
  // Niveaux inférieurs de la carte zoomée (registres J11, J16) : profondeur choisie selon l'écartement des unités de
  // la vue, tous les niveaux préparés d'avance par le serveur.
  useZoomLevelsDriver(zoom, deploymentPending ? undefined : deployment, () => mapRef.current?.getMap() as ZoomMap | undefined);
  const bridges = useBridgeState();
  // Carte vide tant que le dossier n'est pas créé ; ses annotations apparaissent ensuite en fondu.
  const revealed = useMapRevealed();
  // Graphiques tactiques : tracé en cours, glissement du graphique sélectionné.
  const draft = useUi((s) => s.draft);
  const setDraft = useUi((s) => s.setDraft);
  const setGraphicShift = useUi((s) => s.setGraphicShift);
  const setTerrainZone = useUi((s) => s.setTerrainZone);
  const terrain = useUi((s) => s.terrain);
  const setTerrain = useUi((s) => s.setTerrain);
  const view = useUi((s) => s.view);
  const setView = useUi((s) => s.setView);
  const selectedGraphic = useUi((s) => (s.selection?.layer === "graphic" ? s.selection.id : null));
  const graphics = useGraphics();
  // Annotations APP-6 : pose (tracé « annotation »), glissement par temps, menu du clic droit.
  const annotations = useAnnotations();
  const setAnnotationShift = useUi((s) => s.setAnnotationShift);
  const [overSelected, setOverSelected] = useState(false);
  // Bulle d'information au survol d'une unité et mesure de distance et de temps (UnitHover).
  const measuring = useUnitHover((s) => s.measure !== null);
  const lastPoint = useRef<{ x: number; y: number } | null>(null);
  const pointerInside = useRef(false);
  const frameRef = useRef<HTMLDivElement>(null);
  // Manœuvre libre : mode édition du fuseau (poignées, gestes, clavier), menu d'une unité, historique.
  const gestures = useManoeuvreGestures({ mapRef, frameRef, deployment, moveLine });
  const editing = gestures.enabled;
  // Mode « Orienter la manœuvre » (docs/14 §2.14) : glissements d'unités, de LIMA et de lignes de la ZO, édition
  // des graphiques, bulle de survol et menu d'unité suspendus ; clavier de la carte coupé.
  const orienting = useOrientation((s) => s.open);
  // Lignes de la ZO (docs/15 §4.3) : glissement, inclinaison, LIMAR et LIMAV avec le fuseau.
  const measureGestures = useMeasureGestures({ deployment });
  const openUnitMenu = useUnitMenu((s) => s.open);
  useHistoryKeys();
  const longPress = useRef<number | null>(null);
  // Appui long au doigt sur un endroit vide : « Ajouter une annotation ici » (16/10/2026).
  const emptyPress = useRef(createLongPress());
  const suppressClick = useRef(0);
  const lastTap = useRef<{ handle: ManoeuvreHandle; at: number } | null>(null);
  const touchInput = useRef(false);

  const { data: mapDetail } = useQuery({
    queryKey: ["map", mapSlug],
    queryFn: () => api.map(mapSlug!),
    enabled: Boolean(mapSlug),
  });
  // Zone libre affichée (manœuvre libre, étape 2) et tracé de son axe ou de son extension, indépendants du camp.
  const { zone: openZone } = useOpenZone();
  // Mode seulement : la carte ne se redessine pas à chaque mouvement du pointeur pendant le tracé (le bandeau et les
  // couches du tracé suivent l'état complet eux-mêmes).
  const zoneDrawing = useZoneDraw((s) => s.drawing?.mode ?? null);
  // Au doigt, après le premier point : l'aperçu (fuseau prévu, empreinte, puce longueur · surface) suit le centre de
  // la carte, marqué d'un réticule, faute de survol (docs/14 §2.7.3).
  const zoneTouchAim = useZoneDraw((s) => s.drawing?.points.length === 1) && coarsePointer();
  // Bouton « Annotation » et bulle d'astuce (une fois par poste) : mêmes gardes que le clic droit.
  const annotationBlock = annotationMenuBlock({
    revealed,
    hasDeployment: Boolean(deployment),
    editing,
    zoneDrawing: Boolean(zoneDrawing),
    draft: Boolean(draft),
    orienting,
  });
  const savedDossier = useUi((s) => Boolean(s.deploymentParams?.exercise_id));
  const dossierLocked = useDossierLocked();
  const onboarding = useHelp((s) => s.onboarding);
  const welcome = useUi((s) => s.welcome);
  // Bulle : carte du dossier affichée (ni accueil, ni vue plein cadre, ni prise en main), dossier enregistré et
  // modifiable, menu permis ; elle paraît après l'apparition de la carte.
  const tipAllowed =
    annotationBlock === null && savedDossier && !dossierLocked && !onboarding && !welcome && !MAP_COVERING_VIEWS.has(view);
  const tipVisible = useAnnotationTip((s) => s.state === "visible") && tipAllowed;
  useEffect(() => {
    if (!tipAllowed) return;
    const timer = window.setTimeout(() => useAnnotationTip.getState().offer(), ANNOTATION_TIP_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [tipAllowed]);

  /** Point dans l'emprise admise : celle du déploiement (`corridor.bounds`, docs/14 §4.2), sinon celle du camp
   *  (elle tient dans la tolérance du serveur : grille du camp élargie de 10 %). */
  const freeZone = openZone !== null || deployment?.corridor?.origin === "free" || deployment?.corridor?.origin === "zone";
  /** Avis « Hors du terrain » d'un terrain libre : « Étendre le terrain » est proposé et désigné dans la vue Manœuvre. */
  const extendAction = openZone ? extendZoneAction() : undefined;
  const insideZone = ([lon, lat]: [number, number]) => {
    const bbox = deployment?.corridor?.bounds ?? mapDetail?.bbox;
    return !bbox || (lon >= bbox[0] && lon <= bbox[2] && lat >= bbox[1] && lat <= bbox[3]);
  };

  /** Graphique `id` décalé de (dLon, dLat) : tous ses points restent-ils dans le camp ? */
  const graphicInsideCamp = (id: string, dLon: number, dLat: number) => {
    const item = findGraphic(graphics.graphics, id);
    if (!item) return false;
    const points: [number, number][] =
      item.family === "operationalGraphics"
        ? [[item.graphic.longitude, item.graphic.latitude]]
        : item.family === "cbrnPlumes"
          ? [[item.graphic.releaseLon, item.graphic.releaseLat]]
          : item.graphic.coordinates.map((c) => [c.longitude, c.latitude]);
    return points.every(([lon, lat]) => insideZone([lon + dLon, lat + dLat]));
  };

  /** Fin du tracé : le graphique est créé (enregistré dans le dossier) et sa fiche s'ouvre ; une zone d'analyse
   *  du terrain (trois sommets au moins, tracé non croisé) est remise au panneau d'analyse. */
  const finishDraft = (points: [number, number][]) => {
    const current = useUi.getState().draft;
    if (!current) return;
    if (current.purpose === "terrain") {
      if (points.length < 3) {
        setDraft({ ...current, error: t("Posez au moins trois sommets pour délimiter la zone.") });
        return;
      }
      if (isSelfIntersecting(points)) {
        setDraft({ ...current, error: t("Tracé croisé : effacez les derniers sommets (⌫) puis reposez-les.") });
        return;
      }
      setDraft(null);
      setTerrainZone(points);
      return;
    }
    if (current.family === "lineGraphics" && points.length < 2) {
      notify({
        text: t("Ligne inachevée"),
        detail: coarsePointer()
          ? t("Posez au moins deux points, puis touchez « Terminer ».")
          : t("Posez au moins deux points, puis double-cliquez sur le dernier ou appuyez sur Entrée."),
        tone: "info",
      });
      return;
    }
    setDraft(null);
    if (current.family === "annotation") {
      if (points[0]) annotations.create(current.kind, points[0]);
      return;
    }
    graphics.create(current as Draft & { family: GraphicFamily }, points);
  };
  const finishDraftRef = useRef(finishDraft);
  finishDraftRef.current = finishDraft;

  /** Clic pendant un tracé : zone ou panache posé d'un clic ; point suivant d'une ligne (un second clic au même
   *  endroit, celui d'un double-clic, n'ajoute rien). */
  const draftClick = (e: PointerEvt) => {
    const current = useUi.getState().draft;
    if (!current) return;
    const at: [number, number] = [e.lngLat.lng, e.lngLat.lat];
    if (!insideZone(at)) {
      notify({
        text: freeZone ? t("Hors du terrain") : t("Hors du camp"),
        action: extendAction,
        detail:
          current.purpose === "terrain"
            ? freeZone
              ? t("La zone à analyser se trace dans l'emprise des données du terrain.")
              : t("La zone à analyser se trace dans l'emprise des données du camp.")
            : current.family === "annotation"
              ? freeZone
                ? t("Les annotations se posent dans l'emprise du terrain.")
                : t("Les annotations se posent dans l'emprise du camp.")
              : freeZone
                ? t("Les graphiques se dessinent dans l'emprise du terrain.")
                : t("Les graphiques se dessinent dans l'emprise du camp."),
        tone: "warn",
      });
      return;
    }
    if (!isMultiPoint(current)) {
      finishDraft([at]);
      return;
    }
    const map = mapRef.current?.getMap();
    const near = (p: [number, number] | undefined) => {
      if (!p || !map) return false;
      const q = map.project(p);
      return Math.hypot(q.x - e.point.x, q.y - e.point.y) < 8;
    };
    // Zone d'analyse : un clic sur le premier sommet referme la zone et lance l'analyse.
    if (current.purpose === "terrain" && current.points.length >= 3 && near(current.points[0])) {
      finishDraft(current.points);
      return;
    }
    if (near(current.points[current.points.length - 1])) return;
    const points = [...current.points, at];
    if (points.length >= MAX_LINE_POINTS) finishDraft(points);
    else setDraft({ ...current, points, error: undefined });
  };

  // Clavier pendant un tracé : Entrée termine la ligne, Retour arrière efface le dernier point, Échap abandonne.
  const drafting = draft !== null;
  useEffect(() => {
    if (!drafting) return;
    const onKey = (ev: KeyboardEvent) => {
      const target = ev.target as HTMLElement | null;
      if (target && (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))) return;
      const current = useUi.getState().draft;
      if (!current) return;
      if (ev.key === "Escape") {
        if (ev.defaultPrevented) return; // déjà consommé par la pile d'Échap (menu ou fenêtre fermés)
        ev.preventDefault();
        setDraft(null);
      } else if (ev.key === "Enter" && isMultiPoint(current)) {
        ev.preventDefault();
        finishDraftRef.current(current.points);
      } else if ((ev.key === "Backspace" || ev.key === "Delete") && current.points.length) {
        ev.preventDefault();
        setDraft({ ...current, points: current.points.slice(0, -1), error: undefined });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drafting, setDraft]);

  // Tracé de la zone libre : Échap l'abandonne (après la pile partagée : un menu ou une fenêtre se ferment d'abord).
  const zoneDrawingOn = zoneDrawing !== null;
  useEffect(() => {
    if (!zoneDrawingOn) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key !== "Escape" || ev.defaultPrevented) return;
      const target = ev.target as HTMLElement | null;
      if (target && (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))) return;
      ev.preventDefault();
      useZoneDraw.getState().cancelDrawing();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoneDrawingOn]);

  // Glisser-déposer : une unité (vers n'importe quel point) ou une ligne de coordination (le long du
  // fuseau). Le geste commence sur l'objet, ne déplace pas la carte, et n'est enregistré qu'au-delà de
  // 10 m (en deçà, l'iPad le traite comme un simple toucher).
  // Mode édition du fuseau (docs/14 §2.3, §2.4) : sommet (poignée de milieu comprise), extrémité ou courbure de
  // LIMA ; LIMA entière d'un fuseau personnalisé (repère K2), dans ou hors du mode édition. Leur géométrie est
  // calculée par useManoeuvreGestures.
  type Drag =
    | { kind: "unit"; key: string; code: string; phase: Phase; start: [number, number]; last: [number, number] }
    | {
        kind: "line";
        name: LinePreview["name"];
        start: number;
        grab: number;
        last: number;
        blocked: boolean;
        /** Pointeur → abscisse du repère des glissements (`slideMeasure`), créée à la saisie. */
        measure: (point: [number, number]) => number;
      }
    | { kind: "graphic"; id: string; start: [number, number]; last: [number, number] }
    // Annotation APP-6 : position du temps affiché.
    | { kind: "annotation"; id: string; phase: Phase; start: [number, number]; last: [number, number] }
    | {
        kind: "vertex" | "limaEnd" | "limaBend";
        handle: ManoeuvreHandle;
        grab: [number, number];
        at: [number, number];
        session: GestureSession | null;
        /** Paire liée quels que soient ⌥ et « Garder la largeur » (LIMAR, LIMAV saisies en édition). */
        linked?: boolean;
      }
    | { kind: "limaWhole"; name: LinePreview["name"]; grab: [number, number]; session: GestureSession | null }
    // Ligne de la ZO entière (`side` null) ou une de ses extrémités (poignée du mode édition), au temps affiché.
    | { kind: "measure"; key: MeasureKey; phase: Phase; side: 0 | 1 | null; grab: [number, number]; run: MeasureSession | null }
    // LIMAR ou LIMAV hors du mode édition : elles suivent le fuseau, « Modifier le fuseau » est désigné.
    | { kind: "measureBoundary"; key: MeasureKey };
  const dragRef = useRef<{
    drag: Drag;
    point: { x: number; y: number };
    active: boolean;
    /** Geste commencé au doigt : seuil d'activation de 10 px pendant les 500 premières ms. */
    touch: boolean;
    startedAt: number;
  } | null>(null);
  const [grabbing, setGrabbing] = useState(false);

  // Entités sous un point, interrogées à la source : react-map-gl réutilise celles du dernier survol,
  // périmées quand le pointeur arrive sans survoler (toucher, stylet).
  const featuresAt = (point: { x: number; y: number }) => {
    const map = mapRef.current?.getMap() as unknown as
      | {
          getLayer: (id: string) => unknown;
          queryRenderedFeatures: (p: [number, number], o: { layers: string[] }) => PointerEvt["features"];
        }
      | undefined;
    if (!map || !revealed) return [];
    // Seules les couches présentes peuvent être interrogées (les unités n'existent qu'une fois déployées).
    const layers = [UNIT_LAYER_ID, SUBUNIT_LAYER_ID, ANNOTATION_LAYER_ID, LINE_HIT_LAYER_ID, MEASURE_HIT_LAYER_ID, ...GRAPHIC_LAYER_IDS, ...INTERACTIVE_LAYER_IDS].filter(
      (id) => map.getLayer(id),
    );
    // Unités en train de sortir (masquées, retirées) : encore dessinées, plus saisissables.
    const found = layers.length ? (map.queryRenderedFeatures([point.x, point.y], { layers }) ?? []) : [];
    return found.filter((f) => f.properties?.leaving !== true);
  };

  /** Unité sous le pointeur : parmi les symboles touchés, celle dont le centre est le plus proche. */
  const unitUnder = (point: { x: number; y: number }, features: PointerEvt["features"]) => {
    const map = mapRef.current;
    let best: string | null = null;
    let bestDistance = Infinity;
    for (const feature of features ?? []) {
      // Unité en train de sortir (fondu de 400 ms) : plus de bulle.
      if (feature.layer?.id !== UNIT_LAYER_ID || feature.properties?.leaving === true) continue;
      const [lng, lat] = (feature.geometry as { coordinates: [number, number] }).coordinates;
      const at = map?.project([lng, lat]);
      const distance = at ? Math.hypot(at.x - point.x, at.y - point.y) : 0;
      if (distance < bestDistance) {
        bestDistance = distance;
        best = String(feature.properties?.key);
      }
    }
    return best;
  };

  /** Abscisse permise pour une ligne : dans le fuseau, et sans franchir ses voisines (50 m d'écart). */
  const lineBounds = (name: LinePreview["name"]): [number, number] => {
    const corridor = deployment?.corridor;
    if (!corridor) return [0, 0];
    const order = ["L0", "L1", "L2", "L3"];
    const k = order.indexOf(name);
    const present = corridor.lines.filter((l) => l.name !== name);
    const before = present.filter((l) => order.indexOf(l.name) < k).map((l) => l.position_m);
    const after = present.filter((l) => order.indexOf(l.name) > k).map((l) => l.position_m);
    // 1 m de marge sous la règle des 50 m : le serveur recalcule l'abscisse et doit la trouver valide.
    const { low_m: start, high_m: end } = corridor.track;
    const low = before.length ? Math.max(...before) + 51 : start;
    const high = after.length ? Math.min(...after) - 51 : end;
    return [Math.max(start, low), Math.min(end, high)];
  };

  const clearLongPress = () => {
    if (longPress.current !== null) window.clearTimeout(longPress.current);
    longPress.current = null;
    emptyPress.current.cancel();
  };

  /** Gardes du menu « Ajouter une annotation ici », lues à l'instant (clic droit, appui long, bouton). */
  const annotationBlockNow = (): AnnotationMenuBlock =>
    annotationMenuBlock({
      revealed,
      hasDeployment: Boolean(deployment),
      editing,
      zoneDrawing: Boolean(useZoneDraw.getState().drawing),
      draft: Boolean(useUi.getState().draft),
      orienting: useOrientation.getState().open,
    });

  /** Menu « Ajouter une annotation ici » au point `lngLat`, affiché à `at` (fenêtre) ; hors de l'emprise, l'avis le
   *  dit. */
  const openAnnotationMenu = (lngLat: [number, number], at: { x: number; y: number }) => {
    if (annotationBlockNow() !== null) return "blocked" as const;
    if (!insideZone(lngLat)) {
      notify({
        text: freeZone ? t("Hors du terrain") : t("Hors du camp"),
        action: extendAction,
        detail: freeZone
          ? t("Les annotations se posent dans l'emprise du terrain.")
          : t("Les annotations se posent dans l'emprise du camp."),
        tone: "warn",
      });
      return "outside" as const;
    }
    useUnitHover.getState().clear();
    useAnnotationMenu.getState().open({ x: at.x, y: at.y, lngLat });
    return "opened" as const;
  };
  const openAnnotationMenuRef = useRef(openAnnotationMenu);
  openAnnotationMenuRef.current = openAnnotationMenu;

  /** Bouton « Annotation » de la barre du bas : menu ouvert au-dessus du bouton, puis pose au clic sur la carte ;
   *  bloqué par une opération en cours, l'avis dit laquelle terminer et son bandeau est désigné. */
  const annotationButton = (button: HTMLElement) => {
    const block = annotationBlockNow();
    if (block === "hidden") return;
    if (block) {
      const notice = annotationBlockNotice(block);
      designate(notice.target);
      notify({ text: notice.text, detail: notice.detail, tone: "info" });
      return;
    }
    // Le symbole choisi passe en pose : l'utilisateur clique ensuite à l'endroit voulu (même tracé que la palette),
    // l'emprise du camp ou du terrain étant contrôlée à ce clic.
    const r = button.getBoundingClientRect();
    useUnitHover.getState().clear();
    useAnnotationMenu.getState().open({ x: r.left, y: r.top - 4, lngLat: [0, 0], center: true });
  };

  /** Menu d'une unité (clic droit, appui long au doigt) au temps affiché ; aucun en CENTAC. */
  const unitMenu = (key: string, at: { x: number; y: number } | null) => {
    if (deployment?.centac || !at) return false;
    useUnitHover.getState().clear();
    openUnitMenu({ unit: key, phase: useUi.getState().phase, x: at.x, y: at.y });
    return true;
  };

  /** Geste abandonné sans enregistrement (second doigt, toucher annulé par le système, nouveau geste alors que
   *  l'ancien n'a pas reçu son relâcher) : aperçus retirés, poignée rendue, géométrie ramenée. */
  const cancelDrag = () => {
    clearLongPress();
    const current = dragRef.current;
    dragRef.current = null;
    if (!current?.active) return;
    setGrabbing(false);
    if ("session" in current.drag) gestures.abort(current.drag.session);
    else if (current.drag.kind === "measure") measureGestures.abort(current.drag.run);
    else if (current.drag.kind === "unit") setDragPreview(null);
    else if (current.drag.kind === "line") setLinePreview(null);
    else if (current.drag.kind === "graphic") setGraphicShift(null);
    else if (current.drag.kind === "annotation") setAnnotationShift(null);
  };
  const cancelDragRef = useRef(cancelDrag);
  cancelDragRef.current = cancelDrag;

  const beginDrag = (e: PointerEvt, touch = false) => {
    touchInput.current = touch;
    // Pendant un tracé, la carte se déplace librement ; les clics posent les points.
    if (useUi.getState().draft || useZoneDraw.getState().drawing) return;
    // Second doigt : pincement de la carte, le geste en cours est abandonné.
    if (touch && touchCount(e) > 1) {
      cancelDrag();
      return;
    }
    if (dragRef.current) cancelDrag();
    const startedAt = performance.now();
    const grabAt: [number, number] = [e.lngLat.lng, e.lngLat.lat];
    // Au doigt, pas de survol : les sommets mineurs apparaissent autour du toucher.
    if (touch && editing) gestures.touchAt(grabAt);
    // Entités interrogées une seule fois, et seulement si aucune poignée ne l'emporte.
    let queried: NonNullable<PointerEvt["features"]> | null = null;
    const featuresHere = () => (queried ??= featuresAt(e.point) ?? []);
    // Mode édition : une poignée sous le pointeur passe devant tout (rangs −3 à −1, la plus proche l'emporte).
    const target = pickTarget(editing ? gestures.pick(e.point, touch) : null, featuresHere, editing);
    if (target?.kind === "handle" && target.hit.handle.kind === "measureEnd") {
      // Extrémité d'une ligne de la ZO : elle glisse le long de sa limite, la ligne s'incline.
      const handle = target.hit.handle;
      e.preventDefault();
      dragRef.current = {
        drag: { kind: "measure", key: handle.key, phase, side: handle.side === "left" ? 0 : 1, grab: grabAt, run: null },
        point: e.point,
        active: false,
        touch,
        startedAt,
      };
      return;
    }
    if (target?.kind === "handle") {
      const hit = target.hit;
      e.preventDefault();
      const kind =
        hit.handle.kind === "mid"
          ? "vertex"
          : hit.handle.kind === "limaMid"
            ? "limaBend"
            : (hit.handle.kind as Exclude<ManoeuvreHandle["kind"], "mid" | "limaMid" | "measureEnd">);
      dragRef.current = {
        drag: { kind, handle: hit.handle, grab: grabAt, at: hit.at, session: null },
        point: e.point,
        active: false,
        touch,
        startedAt,
      };
      return;
    }
    const feature = target?.kind === "feature" ? target.feature : undefined;
    const layer = feature?.layer?.id;
    if (layer === UNIT_LAYER_ID) {
      e.preventDefault();
      const [lng, lat] = (feature!.geometry as { coordinates: [number, number] }).coordinates;
      const key = String(feature!.properties?.key);
      dragRef.current = {
        drag: {
          kind: "unit",
          key,
          code: String(feature!.properties?.code),
          phase,
          start: [lng, lat],
          last: [lng, lat],
        },
        point: e.point,
        active: false,
        touch,
        startedAt,
      };
      if (touch) {
        // Appui long : sans mouvement au-delà de 10 px pendant 500 ms, le menu de l'unité s'ouvre et le
        // glissement est abandonné.
        clearLongPress();
        const at = clientPoint(e);
        longPress.current = window.setTimeout(() => {
          longPress.current = null;
          const current = dragRef.current;
          if (!current || current.active || current.drag.kind !== "unit" || current.drag.key !== key) return;
          dragRef.current = null;
          if (unitMenu(key, at)) {
            suppressClick.current = performance.now();
            navigator.vibrate?.(10);
          }
        }, LONG_PRESS_MS);
      }
    } else if (layer === ANNOTATION_LAYER_ID && !feature!.properties?.inherited) {
      // Annotation : elle se saisit directement, comme une unité ; le glissement change sa position au temps affiché.
      // Reprise du chef (lecture seule) : pas de glissement, le clic ouvre sa fiche.
      e.preventDefault();
      const [lng, lat] = (feature!.geometry as { coordinates: [number, number] }).coordinates;
      dragRef.current = {
        drag: { kind: "annotation", id: String(feature!.properties?.id), phase, start: [lng, lat], last: [lng, lat] },
        point: e.point,
        active: false,
        touch,
        startedAt,
      };
    } else if (layer === LINE_HIT_LAYER_ID) {
      const name = String(feature!.properties?.name) as LinePreview["name"];
      const corridor = deployment?.corridor;
      const line = corridor?.lines.find((l) => l.name === name);
      if (!line) return;
      e.preventDefault();
      if (corridor?.custom && corridor.model) {
        // Fuseau personnalisé : la LIMA glisse dans le repère du fuseau (inclinaison et courbures gardées).
        dragRef.current = {
          drag: { kind: "limaWhole", name, grab: grabAt, session: null },
          point: e.point,
          active: false,
          touch,
          startedAt,
        };
        return;
      }
      // La ligne suit le déplacement du pointeur depuis l'endroit où elle a été saisie, mesuré sur le repère
      // des glissements : pas de saut au premier mouvement (l'iPad reportait la projection du pointeur sur la
      // corde de la limite comme abscisse curviligne, et la ligne reculait à chaque geste, registre B28).
      // Fuseau automatique : abscisse dont la barre passe sous le pointeur (`slideMeasure`), monotone derrière un
      // coin rentrant de la limite de référence comme sur une amorce en éventail ; la position de la LIMA départage
      // les bandes qui se chevauchent. Le glissement reste rangé dans les glissements automatiques.
      const measure = corridor ? slideMeasure(corridor, line.position_m) : () => line.position_m;
      const grab = measure([e.lngLat.lng, e.lngLat.lat]);
      dragRef.current = {
        drag: { kind: "line", name, start: line.position_m, grab, last: line.position_m, blocked: false, measure },
        point: e.point,
        active: false,
        touch,
        startedAt,
      };
    } else if (layer === MEASURE_HIT_LAYER_ID) {
      const key = String(feature!.properties?.key) as MeasureKey;
      e.preventDefault();
      // LIMAR et LIMAV : en édition, la paire d'extrémité du fuseau glisse, largeur gardée (geste de sommet) ;
      // hors édition, la saisie désigne « Modifier le fuseau ».
      if (key === "LIMAR" || key === "LIMAV") {
        const vertex = editing ? measureGestures.boundaryVertex(key, grabAt) : null;
        dragRef.current = {
          drag: vertex
            ? { kind: "vertex", handle: vertex.handle, grab: grabAt, at: vertex.at, session: null, linked: true }
            : { kind: "measureBoundary", key },
          point: e.point,
          active: false,
          touch,
          startedAt,
        };
        return;
      }
      dragRef.current = {
        drag: { kind: "measure", key, phase, side: null, grab: grabAt, run: null },
        point: e.point,
        active: false,
        touch,
        startedAt,
      };
    } else if (
      selectedGraphic &&
      featuresHere().some((f) => isGraphicLayer(f.layer?.id) && f.properties?.id === selectedGraphic)
    ) {
      // Graphique sélectionné : il se saisit par sa surface ou son contour (un graphique non sélectionné laisse la
      // carte se déplacer, pour ne pas capturer chaque geste à l'intérieur d'une grande zone).
      e.preventDefault();
      const start: [number, number] = [e.lngLat.lng, e.lngLat.lat];
      dragRef.current = {
        drag: { kind: "graphic", id: selectedGraphic, start, last: start },
        point: e.point,
        active: false,
        touch,
        startedAt,
      };
    }
  };

  const continueDrag = (e: PointerEvt) => {
    const current = dragRef.current;
    if (!current) return;
    const { drag } = current;
    if (!current.active) {
      // Seuil d'activation : 4 px ; au doigt, 10 px pendant les 500 premières ms (appui long possible).
      const slop =
        current.touch && performance.now() - current.startedAt < LONG_PRESS_MS ? TOUCH_SLOP_PX : MOUSE_SLOP_PX;
      if (Math.hypot(e.point.x - current.point.x, e.point.y - current.point.y) < slop) return;
      clearLongPress();
      if (drag.kind === "measureBoundary") {
        dragRef.current = null;
        measureGestures.refuseBoundary();
        return;
      }
      if (drag.kind === "measure") {
        // Ligne de la ZO : dossier non enregistré, l'enregistrement est désigné.
        if (!measureGestures.canEdit) {
          dragRef.current = null;
          if (!refuseIfDossierLocked()) measureGestures.refuseNotSaved();
          return;
        }
        drag.run = measureGestures.grab(drag.key, drag.phase, drag.grab, drag.side);
        if (!drag.run) {
          dragRef.current = null;
          return;
        }
        current.active = true;
        setGrabbing(true);
        useUnitHover.getState().clear();
      } else if ("session" in drag) {
        // Poignée du fuseau ou LIMA d'un fuseau personnalisé : dossier non enregistré, l'enregistrement est désigné.
        if (!gestures.canEdit) {
          dragRef.current = null;
          if (!refuseIfDossierLocked()) gestures.refuseNotSaved();
          return;
        }
        drag.session =
          drag.kind === "limaWhole"
            ? gestures.grabLine(drag.name, drag.grab)
            : gestures.grab(drag.handle, drag.grab, drag.at, { linked: drag.linked });
        if (!drag.session) {
          dragRef.current = null;
          return;
        }
        current.active = true;
        setGrabbing(true);
        useUnitHover.getState().clear();
      } else if (drag.kind === "graphic" ? !graphics.canEdit : drag.kind === "annotation" ? !annotations.canEdit : !canMove) {
        // Déplacement refusé tant que le dossier n'est pas enregistré : le bouton « Enregistrer » (ou « Créer
        // le dossier ») des Données initiales clignote pour désigner l'action attendue.
        dragRef.current = null;
        // Dossier en lecture seule pour le compte (docs/17 §3.2) : le bandeau est désigné, pas l'enregistrement.
        if (refuseIfDossierLocked()) return;
        const action =
          drag.kind === "unit"
            ? t("déplacer les unités")
            : drag.kind === "line"
              ? t("déplacer les lignes")
              : drag.kind === "annotation"
                ? t("déplacer les annotations")
                : t("déplacer les graphiques");
        requestSave();
        notify({
          text: exerciseId
            ? t("Enregistrez les modifications du dossier pour {action}", { action })
            : t("Créez le dossier pour {action}", { action }),
          detail: t("Les déplacements sont mémorisés dans le dossier d'exercice."),
          tone: "info",
        });
        return;
      }
      if (!current.active) {
        current.active = true;
        setGrabbing(true);
        useUnitHover.getState().clear();
      }
    }
    if ("session" in drag) {
      if (drag.session) gestures.drag(drag.session, [e.lngLat.lng, e.lngLat.lat], modifiersOf(e));
      return;
    }
    if (drag.kind === "measure") {
      if (drag.run) measureGestures.drag(drag.run, [e.lngLat.lng, e.lngLat.lat]);
      return;
    }
    if (drag.kind === "measureBoundary") return;
    if (drag.kind === "unit") {
      drag.last = [e.lngLat.lng, e.lngLat.lat];
      setDragPreview({ key: drag.key, phase: drag.phase, lngLat: drag.last });
      return;
    }
    if (drag.kind === "graphic") {
      drag.last = [e.lngLat.lng, e.lngLat.lat];
      setGraphicShift({ id: drag.id, dLon: drag.last[0] - drag.start[0], dLat: drag.last[1] - drag.start[1] });
      return;
    }
    if (drag.kind === "annotation") {
      drag.last = [e.lngLat.lng, e.lngLat.lat];
      setAnnotationShift({ id: drag.id, lngLat: drag.last });
      return;
    }
    const corridor = deployment?.corridor;
    if (!corridor) return;
    const wanted = drag.start + drag.measure([e.lngLat.lng, e.lngLat.lat]) - drag.grab;
    const [low, high] = lineBounds(drag.name);
    const clamped = Math.min(Math.max(wanted, low), high);
    // Butée sur une voisine : message une fois par geste, comme l'avis non modal de l'iPad.
    const order = ["L0", "L1", "L2", "L3"];
    const { low_m: start, high_m: end } = corridor.track;
    if (clamped !== wanted && wanted > start && wanted < end && !drag.blocked) {
      drag.blocked = true;
      const k = order.indexOf(drag.name);
      const neighbours = corridor.lines.filter((l) => l.name !== drag.name);
      const blocker =
        wanted > high
          ? neighbours.find((l) => order.indexOf(l.name) > k)?.name
          : [...neighbours].reverse().find((l) => order.indexOf(l.name) < k)?.name;
      if (blocker) {
        flashLineTag(blocker);
        const names = { line: lineLabel(drag.name), other: lineLabel(blocker) };
        notify({
          text:
            wanted > high
              ? t("Ordre des lignes : {line} ne peut être déplacée après {other}", names)
              : t("Ordre des lignes : {line} ne peut être déplacée avant {other}", names),
          tone: "warn",
        });
      }
    }
    drag.last = clamped;
    setLinePreview({ name: drag.name, position_m: clamped });
  };

  const endDrag = useCallback(() => {
    const current = dragRef.current;
    dragRef.current = null;
    clearLongPress();
    if (!current?.active) return;
    setGrabbing(false);
    const { drag } = current;
    if ("session" in drag) {
      if (drag.session) gestures.release(drag.session);
      return;
    }
    if (drag.kind === "measure") {
      if (drag.run) measureGestures.release(drag.run);
      return;
    }
    if (drag.kind === "measureBoundary") return;
    if (drag.kind === "unit") {
      if (metersBetween(drag.start, drag.last) < 10) {
        setDragPreview(null);
        return;
      }
      const [lon, lat] = drag.last;
      select({ layer: "unit", code: drag.code, lngLat: [lon, lat] });
      move.mutate({ unit: drag.key, phase: drag.phase, lon, lat });
      return;
    }
    if (drag.kind === "annotation") {
      if (metersBetween(drag.start, drag.last) < 10) {
        setAnnotationShift(null);
        return;
      }
      if (!insideZone(drag.last)) {
        setAnnotationShift(null);
        notify(
          freeZone
            ? { text: t("Annotation hors du terrain"), detail: t("Les annotations restent dans l'emprise du terrain."), tone: "warn", action: extendAction }
            : { text: t("Annotation hors du camp"), detail: t("Les annotations restent dans l'emprise du camp."), tone: "warn" },
        );
        return;
      }
      select({ layer: "annotation", id: drag.id, lngLat: drag.last });
      // L'aperçu reste à sa place proposée jusqu'à la réponse du serveur.
      annotations.move(drag.id, drag.phase, drag.last, () => setAnnotationShift(null));
      return;
    }
    if (drag.kind === "graphic") {
      const dLon = drag.last[0] - drag.start[0];
      const dLat = drag.last[1] - drag.start[1];
      if (metersBetween(drag.start, drag.last) < 10) {
        setGraphicShift(null);
        return;
      }
      if (!graphicInsideCamp(drag.id, dLon, dLat)) {
        setGraphicShift(null);
        notify(
          freeZone
            ? { text: t("Graphique hors du terrain"), detail: t("Les graphiques restent dans l'emprise du terrain."), tone: "warn", action: extendAction }
            : { text: t("Graphique hors du camp"), detail: t("Les graphiques restent dans l'emprise du camp."), tone: "warn" },
        );
        return;
      }
      // L'aperçu reste décalé jusqu'à la réponse du serveur : pas de retour en arrière à l'écran.
      graphics.move(drag.id, dLon, dLat, () => setGraphicShift(null));
      return;
    }
    const corridor = deployment?.corridor;
    if (!corridor || Math.abs(drag.last - drag.start) < 10) {
      setLinePreview(null);
      return;
    }
    // Point du repère à l'abscisse relâchée (sur la limite de référence hors CENTAC, sur l'axe du camp en
    // CENTAC) : le serveur y retrouve exactement la position de l'aperçu.
    const { origin, axis } = corridor.track;
    const target =
      corridor.track.kind === "axis"
        ? moveM(origin, axis, drag.last)
        : (pointAlongPolyline(corridor.upper, drag.last) ?? moveM(origin, axis, drag.last));
    const center = lineGeometry(corridor, drag.last)[1] ?? target;
    select({ layer: "line", name: drag.name, lngLat: center });
    moveLine.mutate({ line: drag.name, lon: target[0], lat: target[1] });
  }, [deployment, move, moveLine, select, setDragPreview, setLinePreview, setGraphicShift, setAnnotationShift, graphics, annotations, notify, gestures, measureGestures, freeZone]); // eslint-disable-line react-hooks/exhaustive-deps

  // Relâché hors de la carte : le geste se termine quand même, au dernier point survolé.
  useEffect(() => {
    const release = () => {
      if (dragRef.current?.active) endDrag();
      else {
        dragRef.current = null;
        clearLongPress();
      }
    };
    // Toucher annulé par le système (geste d'iPadOS, appel, alerte) : le geste est abandonné, rien n'est enregistré.
    const cancel = () => cancelDragRef.current();
    window.addEventListener("mouseup", release);
    window.addEventListener("touchend", release);
    window.addEventListener("touchcancel", cancel);
    return () => {
      window.removeEventListener("mouseup", release);
      window.removeEventListener("touchend", release);
      window.removeEventListener("touchcancel", cancel);
    };
  }, [endDrag]);

  useEffect(() => {
    const load = config.mapEngine === "mapbox" ? import("./engines/mapbox") : import("./engines/maplibre");
    const controls = config.mapEngine === "mapbox" ? import("react-map-gl/mapbox") : import("react-map-gl/maplibre");
    void Promise.all([load, controls]).then(([mod, rmg]) =>
      setEngine({ ...mod, AttributionControl: rmg.AttributionControl } as unknown as EngineModule),
    );
  }, []);

  // La carte suit la taille de sa zone, pas seulement celle de la fenêtre : Mapbox GL JS ne se redimensionne
  // qu'avec la fenêtre, et la carte restait à son ancienne largeur (bande vide à droite) quand le panneau
  // latéral se repliait. Un redimensionnement par image, pendant l'animation du panneau.
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame || !engine || typeof ResizeObserver === "undefined") return;
    let pending = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(pending);
      pending = requestAnimationFrame(() => mapRef.current?.getMap().resize());
    });
    observer.observe(frame);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(pending);
    };
  }, [engine]);

  const layersOpen = useUi((s) => s.layersOpen);
  const hasSelection = useUi((s) => s.selection !== null);
  const assistantOpen = useUi((s) => s.assistantOpen);
  // Nouvel avis : sa place se mesure à son arrivée (bulle de prise en main ouverte entre-temps, `noticePlace`).
  const noticeId = useUi((s) => s.notice?.id);

  // Bandeau du haut de la carte, un seul à la fois (R10) : tracé > édition du fuseau > rappel de l'analyse du
  // terrain (hors de sa vue).
  const topBanner = topBannerFor({
    zone: zoneDrawing !== null,
    draft: draft !== null,
    editing,
    orienting,
    terrainRecall: Boolean(terrain) && view !== "terrain",
  });

  // Bande libre des bandeaux du haut : entre les panneaux flottants qui longent le haut de la carte, remesurée quand
  // ils changent (ouverture, repli, fiche, fin de leur animation d'entrée) et quand la carte change de taille.
  const [bannerBand, setBannerBand] = useState<BannerPlace | null>(null);
  // Mesure avant l'affichage : le bandeau n'apparaît jamais, même une image, sur un panneau.
  useLayoutEffect(() => {
    const frame = frameRef.current;
    if (!topBanner || !frame) return;
    // Mise à jour seulement si la bande change (un panneau dont la hauteur s'anime en émet beaucoup).
    const measure = () => {
      const next = freeTopBand(frame);
      setBannerBand((current) =>
        current &&
        next &&
        Math.abs(current.center - next.center) < 1 &&
        Math.abs(current.width - next.width) < 1 &&
        current.top === next.top &&
        current.layout === next.layout
          ? current
          : current === next
            ? current
            : next,
      );
    };
    measure();
    const timer = window.setTimeout(measure, 320);
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    observer?.observe(frame);
    document.querySelectorAll<HTMLElement>("[data-floating-panel]").forEach((panel) => observer?.observe(panel));
    return () => {
      window.clearTimeout(timer);
      observer?.disconnect();
    };
  }, [topBanner, layersOpen, hasSelection, assistantOpen, view]);

  // Bande basse : rangée à côté des panneaux quand, passée sur deux lignes (fenêtre étroite), elle croiserait
  // leur bas (calques repliés sur le relevé MGRS), ou entre les coins du fond de carte (échelle, logo,
  // attribution) quand, centrée, elle les recouvrirait ; remesurée comme la bande du bandeau. L'avis se pose
  // au-dessus d'elle, dans la bande libre entre les panneaux.
  const bottomRef = useRef<HTMLDivElement>(null);
  const [bottomBand, setBottomBand] = useState<Band | null>(null);
  const [notice, setNotice] = useState<{ bottom: number; band: Band | null }>({ bottom: 68, band: null });
  useEffect(() => {
    const frame = frameRef.current;
    const bars = bottomRef.current;
    if (!frame || !bars) return;
    // `fromBars` : les barres seules ont changé de taille (rangement dans la bande, relevé masqué) ; elles ne
    // quittent alors pas la bande, sans quoi elles pourraient y entrer et en sortir sans fin. Elles la quittent
    // quand la carte ou les panneaux changent et qu'elles y tiennent sur une ligne.
    const measure = (fromBars: boolean) => {
      const { height, width } = bars.getBoundingClientRect();
      setBottomBand((current) => {
        const next = freeBottomBand(frame, height, width);
        // Barres seules redimensionnées : la bande ne s'élargit jamais. Rangées sur une ligne dans une bande étroite
        // (relevés masqués), elles ne croisent plus la fiche ; élargie, la bande les remettrait sur deux lignes, qui
        // la croisent, d'où un va-et-vient à chaque image (fiche ouverte, iPad paysage). Elle se resserre seulement ;
        // un changement de la carte ou des panneaux la recalcule entière.
        if (fromBars && current && (!next || next.width > current.width)) return current;
        if (current && next && Math.abs(current.center - next.center) < 1 && Math.abs(current.width - next.width) < 1)
          return current;
        return current === next ? current : next;
      });
      const place = freeNoticePlace(frame, height);
      setNotice((current) =>
        current.bottom === place.bottom &&
        (current.band === place.band ||
          (current.band &&
            place.band &&
            Math.abs(current.band.center - place.band.center) < 1 &&
            Math.abs(current.band.width - place.band.width) < 1))
          ? current
          : place,
      );
    };
    measure(false);
    const timer = window.setTimeout(() => measure(false), 320);
    const layout = typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => measure(false)) : null;
    const own = typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => measure(true)) : null;
    layout?.observe(frame);
    own?.observe(bars);
    document.querySelectorAll<HTMLElement>("[data-floating-panel]").forEach((panel) => layout?.observe(panel));
    // Coins du fond de carte : l'attribution se déplie et se replie, l'échelle change de largeur avec le zoom.
    mapCorners(frame).forEach((corner) => layout?.observe(corner));
    return () => {
      window.clearTimeout(timer);
      layout?.disconnect();
      own?.disconnect();
    };
  }, [layersOpen, hasSelection, assistantOpen, view, editing, mapLoaded, noticeId]);

  // CENTAC : la vue se cadre sur l'emprise du camp de Mailly, resserrée de 8 % par côté, comme sur l'iPad
  // (MapLibreMapView.fitCentacCampBounds) ; sinon sur la grille du camp.
  const campBounds = useMemo(() => (deployment?.centac ? CENTAC_FRAME : null), [deployment?.centac]);

  const zoneBbox = openZone?.bbox ?? null;
  // Niveau compagnie (docs/21) : « Centrer » et le cadrage du camp visent le dispositif du S/GTIA, dans sa zone de
  // quelques kilomètres, au zoom d'entrée du niveau, et non l'emprise de tout le camp (revue du 08/10/2026).
  const frameCompany = useCallback((): boolean => {
    const map = mapRef.current;
    if (!map || !deployment || !isCompany(deployment.level) || deployment.map_slug !== mapSlug) return false;
    const center = unitsCentroid(deployment.units, phase);
    if (!center) return false;
    const container = map.getContainer();
    const pad = freePadding(container);
    const width = Math.max(320, container.clientWidth - pad.left - pad.right);
    const height = Math.max(260, container.clientHeight - pad.top - pad.bottom);
    const zoom = fittingZoom(deployment.units, phase, center, width, height, entryZoom(deployment));
    map.easeTo({ center, zoom, padding: pad, duration: 900 });
    return true;
  }, [deployment, mapSlug, phase]);
  const fitCamp = useCallback(() => {
    if (frameCompany()) return;
    const bbox = campBounds ?? mapDetail?.bbox ?? zoneBbox;
    if (!bbox || !mapRef.current) return;
    // Les panneaux flottants masquent le bord gauche (vue + calques) et droit (fiche d'objet) :
    // on cadre le camp dans la partie réellement visible de la carte.
    // Mapbox GL refuse de cadrer si les marges dépassent la carte (fenêtre étroite) : elles sont plafonnées.
    // Calques repliés : une simple barre en haut, qu'on évite par la marge haute plutôt que par la gauche.
    const width = mapRef.current.getContainer().clientWidth;
    const left = Math.min(12 + 392 + (layersOpen ? 312 : 0) + 24, width * 0.55);
    const right = Math.min(hasSelection ? 356 : 40, width * 0.2);
    const top = layersOpen ? 40 : 12 + 36 + 16;
    mapRef.current.fitBounds(
      [
        [bbox[0], bbox[1]],
        [bbox[2], bbox[3]],
      ],
      { padding: { top, bottom: 80, left, right }, duration: 900 },
    );
  }, [campBounds, mapDetail?.bbox, zoneBbox, layersOpen, hasSelection, frameCompany]);

  // Cadrage automatique à chaque changement de camp (et seulement dans ce cas), puis en CENTAC dès que
  // l'emprise du camp est connue pour la force affichée.
  const fitCampRef = useRef(fitCamp);
  fitCampRef.current = fitCamp;

  // Glisser une unité depuis une liste (docs/14 §2.6) : la carte convertit le point de dépôt et contrôle l'emprise
  // admise ; seul un point du fond de carte (pas un panneau, une barre ou une fiche au-dessus) est un dépôt.
  const insideZoneRef = useRef(insideZone);
  insideZoneRef.current = insideZone;
  const freeZoneRef = useRef(freeZone);
  freeZoneRef.current = freeZone;
  useEffect(() => {
    if (!mapLoaded) return;
    return registerMapDrop({
      locate: (x, y) => {
        const frame = frameRef.current;
        const map = mapRef.current;
        const element = document.elementFromPoint(x, y);
        if (!frame || !map || !(element instanceof HTMLCanvasElement) || !frame.contains(element)) return null;
        const box = frame.getBoundingClientRect();
        const at = map.unproject([x - box.left, y - box.top]);
        return [at.lng, at.lat];
      },
      inside: (at) => insideZoneRef.current(at),
      center: () => {
        const at = mapRef.current?.getCenter();
        return at ? [at.lng, at.lat] : null;
      },
      fit: () => fitCampRef.current(),
      freeZone: () => freeZoneRef.current,
    });
  }, [mapLoaded]);

  // Lieu nommé d'un nouveau terrain (demande d'Alexis du 01/11/2026) : à chaque ouverture de la vue Manœuvre, et quand
  // le lieu change, la carte rejoint le lieu du dossier ou de son brouillon, une fois chargée, au zoom du niveau, dans
  // la partie que les panneaux laissent libre (`placeFraming.ts`). Tant que la vue reste ouverte sur la même carte, les
  // cadrages automatiques ci-dessous (dispositif, camp, terrain ouvert) s'abstiennent (`placeHeld`) : arrivés après
  // (unités ou camp servis plus tard, carte chargée ensuite), ils écrasaient le centrage.
  const placeHeld = usePlaceFraming(mapLoaded, ({ center, zoom }) => {
    const map = mapRef.current;
    if (!map) return;
    map.flyTo({ center, zoom, padding: freePadding(map.getContainer()), duration: prefersReducedMotion() ? 0 : 1000 });
  });

  // Cadrage sur le dispositif (demande d'Alexis du 01/10/2026, comme l'iPad) : une fois par dossier et par camp,
  // au premier affichage des unités (création du dossier, ouverture, changement de camp), la carte se centre sur
  // le barycentre des unités AMI et ENI, au zoom d'entrée de l'iPad, dans la partie que les panneaux flottants
  // laissent libre ; la rotation CENTAC garde l'emprise du camp. Ni les bascules de temps ni un déplacement de
  // la carte par l'utilisateur ne la recadrent ensuite. Un camp choisi et enregistré dans les Données initiales
  // (nouveau dossier ou changement de camp, `frameDossier`, demande d'Alexis du 03/10/2026) le recadre une fois de
  // plus, après l'enregistrement, même si son aperçu l'avait déjà cadré.
  const frameRequest = useUi((s) => s.frameRequest);
  const recenterKey = exerciseId && mapSlug ? `${exerciseId}|${mapSlug}|${frameRequest}` : null;
  const recentered = useRef<string | null>(null);
  useEffect(() => {
    const map = mapRef.current;
    if (!recenterKey || recentered.current === recenterKey || !engine || !map) return;
    if (!deployment || deploymentPending || deployment.map_slug !== mapSlug) return;
    const center = unitsCentroid(deployment.units, phase);
    if (!center) return; // pas encore d'unité à la carte : le cadrage attend son apparition
    recentered.current = recenterKey;
    if (deployment.centac) return; // emprise du camp, déjà cadrée
    if (placeHeld()) return; // lieu nommé rejoint à l'ouverture de la vue Manœuvre : il garde la vue
    const container = map.getContainer();
    let pad = freePadding(container);
    let width = container.clientWidth - pad.left - pad.right;
    let height = container.clientHeight - pad.top - pad.bottom;
    if (width < 320 || height < 260) {
      // Tablette en portrait, panneau ouvert : presque plus de carte libre. Cadrage sur toute la carte, au
      // zoom de l'iPad ; les unités sont en place dès que le panneau se replie.
      pad = { top: 60, bottom: 90, left: 24, right: 24 };
      width = container.clientWidth - pad.left - pad.right;
      height = container.clientHeight - pad.top - pad.bottom;
    }
    // Marge de la zone libre donnée à la caméra (et non un décalage : la carte garde la marge du dernier cadrage
    // du camp, qu'un décalage viendrait doubler).
    map.easeTo({
      center,
      zoom: fittingZoom(deployment.units, phase, center, width, height, entryZoom(deployment)),
      padding: pad,
      duration: 900,
    });
  }, [recenterKey, deployment, deploymentPending, phase, engine, mapSlug]);

  useEffect(() => {
    // Dossier déjà cadré sur son dispositif (unités arrivées avant le camp) : le camp ne le décadre pas.
    if (placeHeld()) return;
    if (mapDetail?.slug && !(recenterKey && recentered.current === recenterKey)) fitCampRef.current();
  }, [mapDetail?.slug, engine]);
  // Zone libre : cadrage sur son empreinte dès qu'elle est connue (zone créée ou ouverte, terrain pas encore servi).
  const zoneFrame = openZone && zoneBbox ? openZone.id : null;
  useEffect(() => {
    if (zoneFrame && !placeHeld() && !(recenterKey && recentered.current === recenterKey)) fitCampRef.current();
  }, [zoneFrame, engine]); // eslint-disable-line react-hooks/exhaustive-deps
  const centacFrame = campBounds && deployment ? `${deployment.map_slug}|${deployment.force_ref}` : null;
  useEffect(() => {
    if (centacFrame) fitCampRef.current();
  }, [centacFrame, engine]);

  useEffect(() => {
    if (flyTo && mapRef.current) mapRef.current.flyTo({ center: flyTo.lngLat, zoom: flyTo.zoom, duration: 800 });
  }, [flyTo]);

  // Itinéraire de l'assistant : cadré dans la partie de la carte que les panneaux flottants laissent libre.
  const assistantRoute = useUi((s) => s.assistantRoute);
  useEffect(() => {
    if (!assistantRoute || !mapRef.current) return;
    mapRef.current.fitBounds(assistantRoute.bounds, {
      padding: freePadding(mapRef.current.getContainer()),
      duration: 900,
      maxZoom: 14,
    });
  }, [assistantRoute]);

  // Mode orientation : cadrage unique à l'entrée sur la cible du dispositif, dans la bande libre, seulement si le centre
  // ou la cible n'y sont pas déjà entièrement.
  useEffect(() => {
    const map = mapRef.current?.getMap();
    const block = orienting ? deployment?.corridor?.orientation : null;
    if (!map || !block?.target) return;
    const [w, s, e, n] = block.target;
    const container = map.getContainer();
    const pad = freePadding(container);
    const { width, height } = container.getBoundingClientRect();
    const inside = ([lon, lat]: [number, number]) => {
      const p = map.project([lon, lat]);
      return p.x >= pad.left && p.x <= width - pad.right && p.y >= pad.top && p.y <= height - pad.bottom;
    };
    const corners: [number, number][] = [[w, s], [e, s], [e, n], [w, n]];
    if (corners.every(inside)) return;
    map.fitBounds([[w, s], [e, n]], { padding: pad, duration: prefersReducedMotion() ? 0 : 900, maxZoom: 13 });
    // Une seule fois, à l'entrée dans le mode.
  }, [orienting]); // eslint-disable-line react-hooks/exhaustive-deps

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  // Zone libre hors de France : fond Mapbox imposé, sans écraser la préférence mémorisée (docs/14 §2.7.10).
  const forced = forcedBasemap({
    engine: config.mapEngine,
    kind: openZone ? "user" : mapDetail?.kind,
    bbox: openZone?.bbox ?? mapDetail?.bbox,
  });
  const effectiveBasemap = basemapShown(config.mapEngine, basemap, forced);
  // Couches d'une zone libre : relues quand sa préparation ou son extension change le terrain (même adresse sinon).
  // Dossier ouvert (comptes nominatifs, docs/17 §3.4) : le terrain d'un dossier partagé reste lisible même privé.
  const layerStamp =
    (openZone ? `?v=${encodeURIComponent(openZone.prepared_at ?? openZone.status)}` : "") +
    dossierParam("exercise", openZone ? "&" : "?");
  // Mention des sources (ODbL) d'une zone libre ; « » tant que la zone n'a pas encore la sienne (en préparation).
  const zoneAttribution = openZone ? (openZone.attribution ?? mapDetail?.attribution ?? "") : null;
  const mapStyle = useMemo(
    () => (config.mapEngine === "mapbox" ? config.mapboxStyle : maplibreBaseStyle()),
    [],
  );

  if (!engine) {
    return <div className="absolute inset-0 bg-canvas" />;
  }
  const { Map, Source, Layer, Marker, ScaleControl, AttributionControl } = engine;

  return (
    <div
      ref={frameRef}
      className="absolute inset-0 select-none"
      // Appui long au doigt : ni loupe ni menu du système (le menu de l'unité s'ouvre à sa place).
      style={{ WebkitTouchCallout: "none" }}
      onMouseEnter={() => {
        pointerInside.current = true;
      }}
      onMouseLeave={() => {
        // Pointeur sorti de la carte : la bulle disparaît ; une mesure reste affichée jusqu'à son effacement. Le
        // relevé MGRS garde sa place et sa dernière coordonnée, estompée, jusqu'au retour du curseur.
        pointerInside.current = false;
        useUnitHover.getState().setHover(null);
        setCursor("—");
      }}
    >
      <Map
        attributionControl={false}
        ref={(ref: unknown) => {
          mapRef.current = ref as typeof mapRef.current;
          const instance = mapRef.current?.getMap();
          if (instance) provideMissingImages(instance as unknown as SymbolHost);
          // Capture de la vue d'une anomalie signalée (registre F39) : lecture de l'image rendue, rien d'autre.
          setCaptureMap(instance ? (instance as unknown as MapSource) : null);
          // Accès de diagnostic en développement uniquement.
          if (import.meta.env.DEV) (window as unknown as { __gxMap?: unknown }).__gxMap = mapRef.current?.getMap();
        }}
        mapboxAccessToken={config.mapboxToken || undefined}
        // Tuiles et couches de l'API : en-tête du compte monté, comme toute requête de l'application (docs/17 §4.8).
        transformRequest={mapRequest as never}
        initialViewState={FRANCE_VIEW}
        mapStyle={mapStyle as never}
        style={{ position: "absolute", inset: 0 }}
        interactiveLayerIds={
          revealed
            ? [UNIT_LAYER_ID, SUBUNIT_LAYER_ID, ANNOTATION_LAYER_ID, LINE_HIT_LAYER_ID, MEASURE_HIT_LAYER_ID, ...GRAPHIC_LAYER_IDS, ...INTERACTIVE_LAYER_IDS]
            : []
        }
        onLoad={(e: { target: SymbolHost }) => {
          // Les symboles APP-6D sont dessinés à la demande, au premier affichage de chaque code SIDC.
          const map = e.target;
          setMapLoaded(true);
          provideMissingImages(map);
          void loadMissionImages(map as unknown as Parameters<typeof loadMissionImages>[0]);
          // Dossier rouvert à la relance : son camp a pu arriver avant la carte, on le cadre maintenant.
          fitCampRef.current();
        }}
        onMouseMove={(e) => {
          setCursor(formatMgrs(e.lngLat.lng, e.lngLat.lat));
          lastPoint.current = e.point;
          const hover = useUnitHover.getState();
          if (useOrientation.getState().open) {
            hover.clear();
            return;
          }
          if (useZoneDraw.getState().drawing) {
            hover.clear();
            useZoneDraw.getState().hover([e.lngLat.lng, e.lngLat.lat]);
            return;
          }
          const current = useUi.getState().draft;
          if (current) {
            hover.clear();
            setDraft({ ...current, hover: [e.lngLat.lng, e.lngLat.lat] });
            return;
          }
          continueDrag(e as unknown as PointerEvt);
          const features = (e as unknown as PointerEvt).features ?? [];
          // Mode édition : poignée survolée (curseur, grossissement) ; la bulle des unités est alors suspendue.
          const handle = editing ? gestures.hoverAt(e.point, [e.lngLat.lng, e.lngLat.lat]) : null;
          // Survol : bulle de l'unité sous le pointeur ; mesure armée : la flèche suit le pointeur.
          if (dragRef.current?.active) hover.clear();
          else if (hover.measure) hover.aim([e.lngLat.lng, e.lngLat.lat]);
          else hover.setHover(handle ? null : unitUnder(e.point, features));
          // Main ouverte sur le graphique sélectionné : il se déplace d'un glisser.
          setOverSelected(
            selectedGraphic !== null &&
              features.some((f) => isGraphicLayer(f.layer?.id) && f.properties?.id === selectedGraphic),
          );
        }}
        onDblClick={(e) => {
          if (useOrientation.getState().open) return;
          const current = useUi.getState().draft;
          if (current && isMultiPoint(current)) {
            e.preventDefault();
            finishDraft(current.points);
            return;
          }
          // Mode édition : le double-clic sur un sommet retire sa paire (deux au moins), sur une courbure la
          // retire ; jamais de zoom (désactivé en édition).
          const target = pickTarget(editing ? gestures.pick(e.point) : null, [], editing);
          if (target?.kind === "handle") {
            e.preventDefault();
            gestures.removeHandle(target.hit.handle);
          }
        }}
        onMouseDown={(e) => {
          if (!useOrientation.getState().open) beginDrag(e as unknown as PointerEvt);
        }}
        onTouchStart={(e) => {
          if (useOrientation.getState().open) return;
          beginDrag(e as unknown as PointerEvt, true);
          // Endroit vide (aucun objet saisi par le doigt) : l'appui long ouvre « Ajouter une annotation ici » au point
          // touché ; un mouvement de plus de 10 px, un second doigt, le doigt levé ou la carte qui bouge l'annulent.
          if (dragRef.current || touchCount(e) > 1 || annotationBlockNow() !== null) return;
          const at = clientPoint(e);
          if (!at) return;
          const lngLat: [number, number] = [e.lngLat.lng, e.lngLat.lat];
          emptyPress.current.start(e.point, touchCount(e), () => {
            if (dragRef.current) return;
            const result = openAnnotationMenuRef.current(lngLat, at);
            if (result === "blocked") return;
            // Le toucher qui termine l'appui ne sélectionne rien (pont, route…).
            suppressClick.current = performance.now();
            if (result === "opened") navigator.vibrate?.(10);
          });
        }}
        onTouchMove={(e) => {
          emptyPress.current.move(e.point, touchCount(e));
          continueDrag(e as unknown as PointerEvt);
        }}
        onContextMenu={(e) => {
          // Clic droit sur une unité : menu de mission du temps affiché, « Position doctrinale », « Centrer »,
          // « Mesurer depuis cette unité » (rendu par les panneaux).
          if (useUi.getState().draft || useOrientation.getState().open) return;
          const feature = topFeature(featuresAt(e.point));
          const original = (e as unknown as { originalEvent?: Event }).originalEvent;
          if (feature?.layer?.id === UNIT_LAYER_ID) {
            if (unitMenu(String(feature.properties?.key), clientPoint(e))) {
              e.preventDefault();
              original?.preventDefault();
            }
            return;
          }
          // Ailleurs sur la carte du dossier : « Ajouter une annotation ici » (exception à la carte figée, 15/10/2026),
          // mêmes gardes que le bouton « Annotation » et l'appui long au doigt (`annotationMenuBlock`).
          const at = clientPoint(e);
          if (!at || annotationBlockNow() !== null) return;
          e.preventDefault();
          original?.preventDefault();
          openAnnotationMenu([e.lngLat.lng, e.lngLat.lat], at);
        }}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => {
          gestures.leave();
          setHovering(false);
          setOverSelected(false);
          const current = useUi.getState().draft;
          if (current?.hover) setDraft({ ...current, hover: null });
          useZoneDraw.getState().hover(null);
        }}
        onZoom={(e) => setZoom(e.viewState.zoom)}
        onRotate={(e) => setBearing(Math.round(e.viewState.bearing / 5) * 5)}
        // La bulle et la mesure sont posées à l'écran : un déplacement de la carte les retire ; à l'arrêt, la
        // bulle revient si le pointeur est resté sur une unité.
        onMoveStart={() => {
          useUnitHover.getState().clear();
          emptyPress.current.cancel();
        }}
        onMove={(e: { target: { getCenter: () => { lng: number; lat: number } } }) => {
          // Tracé de zone au doigt : l'aperçu suit le réticule du centre pendant que la carte glisse.
          if (useZoneDraw.getState().drawing?.points.length !== 1 || !coarsePointer()) return;
          const center = e.target.getCenter();
          useZoneDraw.getState().hover([center.lng, center.lat]);
        }}
        onMoveEnd={() => {
          const point = lastPoint.current;
          if (!point || !pointerInside.current || dragRef.current || useUi.getState().draft) return;
          if (useOrientation.getState().open) return;
          useUnitHover.getState().setHover(unitUnder(point, featuresAt(point)));
        }}
        onClick={(e) => {
          // Mode orientation : la carte se déplace et se zoome, sans rien sélectionner.
          if (useOrientation.getState().open) return;
          // Tracé de la zone libre : premier point, puis axe (ou emprise) contrôlé ; accepté, il attend sa
          // confirmation dans la vue Manœuvre ; refusé, la règle est dite dans le bandeau.
          const zoneClick = clickZoneDraw([e.lngLat.lng, e.lngLat.lat]);
          if (zoneClick === "placed" && coarsePointer()) {
            const center = mapRef.current?.getMap().getCenter();
            if (center) useZoneDraw.getState().hover([center.lng, center.lat]);
          }
          if (zoneClick) {
            if (zoneClick === "accepted") {
              setView("manoeuvre");
              designateSoon('[data-attention="zone-confirm"]');
            }
            return;
          }
          if (useUi.getState().draft) {
            draftClick(e as unknown as PointerEvt);
            return;
          }
          // Toucher qui termine un appui long (menu de l'unité ouvert) : rien d'autre.
          if (performance.now() - suppressClick.current < 800) return;
          // Mode édition : un clic sélectionne la poignée ; deux touchers rapprochés au doigt la retirent.
          const target = pickTarget(
            editing ? gestures.pick(e.point, touchInput.current) : null,
            () => featuresAt(e.point),
            editing,
          );
          if (target?.kind === "handle" && target.hit.handle.kind === "measureEnd") {
            // Extrémité d'une ligne de la ZO : sa fiche s'ouvre.
            const { key, phase: linePhase } = target.hit.handle;
            select({ layer: "measure", key, phase: linePhase, lngLat: target.hit.at });
            return;
          }
          if (target?.kind === "handle") {
            const hit = target.hit;
            const now = performance.now();
            const previousTap = lastTap.current;
            lastTap.current = { handle: hit.handle, at: now };
            if (
              touchInput.current &&
              previousTap &&
              now - previousTap.at < DOUBLE_TAP_MS &&
              JSON.stringify(previousTap.handle) === JSON.stringify(hit.handle)
            ) {
              lastTap.current = null;
              gestures.removeHandle(hit.handle);
              return;
            }
            gestures.select(hit.handle);
            return;
          }
          if (editing) gestures.select(null);
          const feature = target?.kind === "feature" ? target.feature : undefined;
          // Alt + clic sur une unité : mesure de distance et de temps depuis l'unité (un second Alt + clic sur
          // la même unité la termine) ; pendant une mesure, un clic la termine sans rien sélectionner.
          const hover = useUnitHover.getState();
          const altKey = Boolean((e as unknown as { originalEvent?: MouseEvent }).originalEvent?.altKey);
          if (altKey && feature?.layer?.id === UNIT_LAYER_ID) {
            const key = String(feature.properties?.key);
            if (hover.measure?.key === key) hover.disarm();
            else hover.arm(key);
            return;
          }
          if (hover.measure) {
            hover.disarm();
            return;
          }
          if (feature?.layer?.id === UNIT_LAYER_ID) {
            const [lng, lat] = (feature.geometry as unknown as { coordinates: [number, number] }).coordinates;
            select({ layer: "unit", code: String(feature.properties?.code), lngLat: [lng, lat] });
            return;
          }
          // Unité d'un niveau inférieur (carte zoomée) : sa fiche, en lecture seule.
          if (feature?.layer?.id === SUBUNIT_LAYER_ID) {
            const [lng, lat] = (feature.geometry as unknown as { coordinates: [number, number] }).coordinates;
            select({ layer: "subunit", key: String(feature.properties?.key), lngLat: [lng, lat] });
            return;
          }
          if (feature?.layer?.id === ANNOTATION_LAYER_ID && feature.properties?.id) {
            const [lng, lat] = (feature.geometry as unknown as { coordinates: [number, number] }).coordinates;
            select({ layer: "annotation", id: String(feature.properties.id), lngLat: [lng, lat] });
            return;
          }
          if (feature?.layer?.id === LINE_HIT_LAYER_ID) {
            const name = String(feature.properties?.name) as LinePreview["name"];
            // En édition, la LIMA cliquée montre ses courbures et les milieux de son tracé.
            if (editing) useManoeuvre.getState().selectLine(name);
            select({ layer: "line", name, lngLat: [e.lngLat.lng, e.lngLat.lat] });
            return;
          }
          // Ligne de la ZO : sa fiche s'ouvre (au temps affiché pour la FLOT et la CFL).
          if (feature?.layer?.id === MEASURE_HIT_LAYER_ID) {
            const key = String(feature.properties?.key) as MeasureKey;
            const linePhase = (feature.properties?.phase as Phase | null | undefined) ?? null;
            select({ layer: "measure", key, phase: linePhase, lngLat: [e.lngLat.lng, e.lngLat.lat] });
            return;
          }
          // Pont : un clic le détruit (ou le rétablit), comme sur l'iPad ; sa fiche s'ouvre avec son état.
          if (feature?.layer?.id === BRIDGE_LAYER_ID && feature.properties?.key) {
            const properties = feature.properties;
            bridges.toggle({ key: String(properties.key), name: String(properties.name ?? t("Pont")) });
            select({ layer: "bridges", lngLat: [e.lngLat.lng, e.lngLat.lat], properties });
            return;
          }
          if (isGraphicLayer(feature?.layer?.id) && feature?.properties?.id) {
            select({ layer: "graphic", id: String(feature.properties.id), lngLat: [e.lngLat.lng, e.lngLat.lat] });
            return;
          }
          const key = feature?.layer?.id ? layerKeyOf(feature.layer.id) : null;
          select(
            feature && key
              ? { layer: key, lngLat: [e.lngLat.lng, e.lngLat.lat], properties: feature.properties ?? {} }
              : null,
          );
        }}
        cursor={
          draft || measuring || zoneDrawing
            ? "crosshair"
            : (gestures.cursor ??
              (grabbing ? "grabbing" : overSelected ? "grab" : hovering ? "pointer" : "default"))
        }
        dragRotate={false}
        doubleClickZoom={!draft && !editing && !zoneDrawing}
        // Mode édition : le clavier du moteur est coupé ; les flèches déplacent le sommet sélectionné (⇧← ne fait
        // pas tourner la carte), Tab parcourt les sommets (useManoeuvreGestures).
        keyboard={!editing && !orienting}
      >
        {/* Éclaircissement : voile blanc au-dessus de tous les fonds (style Mapbox, plan, ortho),
            sous les couches Generex. Premier enfant de la carte : react-map-gl crée les couches dans l'ordre du
            rendu, le voile existe donc avant les fonds qui s'insèrent juste dessous (`beforeId`) ; les couches
            Generex viennent au-dessus. */}
        <Layer
          id={VEIL_ID}
          type="background"
          layout={{ visibility: mapLighten > 0 ? "visible" : "none" }}
          paint={{ "background-color": "#ffffff", "background-opacity": mapLighten }}
        />
        {/* Fonds IGN (Plan désaturé comme sur l'iPad, ou Ortho), sous les couches Generex. */}
        <Source id="ign-plan" type="raster" tiles={[config.ignPlanUrl]} tileSize={256} maxzoom={18}
          attribution="© IGN Géoplateforme">
          <Layer
            id="ign-plan"
            type="raster"
            beforeId={VEIL_ID}
            layout={{ visibility: effectiveBasemap === "ign-plan" ? "visible" : "none" }}
            // Réglages du plan IGN de l'iPad (style-plan-ign.json).
            paint={{
              "raster-saturation": -0.6,
              "raster-brightness-min": 0.05,
              "raster-contrast": -0.08,
              "raster-opacity": 0.85,
            }}
          />
        </Source>
        <Source id="ign-ortho" type="raster" tiles={[config.ignOrthoUrl]} tileSize={256} maxzoom={19}
          attribution="© IGN Géoplateforme">
          <Layer
            id="ign-ortho"
            type="raster"
            beforeId={VEIL_ID}
            layout={{ visibility: effectiveBasemap === "ign-ortho" ? "visible" : "none" }}
            // Réglages de l'ortho de l'iPad (style-ortho-ign.json).
            paint={{ "raster-saturation": -0.15, "raster-contrast": -0.05, "raster-brightness-max": 0.92 }}
          />
        </Source>

        {mapSlug &&
          LAYER_ORDER.map((key: LayerKey) => (
            <Source key={`${mapSlug}-${key}`} id={`gx-${key}`} type="geojson"
              data={`${origin}/api/maps/${mapSlug}/layers/${key}${layerStamp}`}>
              {LAYER_STYLES[key].map((style) => (
                <Layer
                  key={style.id}
                  id={style.id}
                  type={style.type as never}
                  // Mapbox GL refuse une borne de zoom `undefined` (MapLibre l'ignore) : la clé n'est posée que si elle existe.
                  {...(style.minzoom !== undefined ? { minzoom: style.minzoom } : {})}
                  // Corps d'armée (docs/08 §23) : les ponts des grands axes de 300 × 600 km se recouvraient aux échelles
                  // du corps ; ils apparaissent à partir du zoom 9, celui d'une division.
                  {...(style.id === BRIDGE_LAYER_ID && deployment?.level === "CORPS" ? { minzoom: CORPS_BRIDGE_MINZOOM } : {})}
                  paint={revealPaint(style.type, style.paint, revealed) as never}
                  layout={
                    {
                      ...(style.layout ?? {}),
                      ...(style.id === BRIDGE_LAYER_ID ? { "icon-image": bridgeImageExpression(bridges.destroyed) } : {}),
                      // Terrain libre : cours d'eau masqués par défaut (le fond de carte les montre déjà).
                      visibility: (key === "rivers" && openZone ? freeTerrainRivers : visible[key]) ? "visible" : "none",
                    } as never
                  }
                />
              ))}
            </Source>
          ))}

        {/* Graphiques tactiques montés dès le camp choisi : leurs couches restent sous le fuseau et les unités,
            ajoutées ensuite ; l'aperçu d'un tracé, monté à la demande, vient au-dessus de tout. */}
        {mapSlug && <GraphicsLayers Source={Source} Layer={Layer} Marker={Marker} zoom={zoom} bearing={bearing} />}
        {/* Frontières des pays fictifs de la fiche pays (seule exception à la carte figée, 11/10/2026) : sous les
            graphiques tactiques (beforeId), au-dessus du fond et des couches du camp. */}
        {mapSlug && <CountryBordersLayers Source={Source} Layer={Layer} />}
        {/* Analyse du terrain : ses couches s'insèrent sous les graphiques (beforeId), quel que soit leur montage. */}
        {mapSlug && <TerrainLayers Source={Source} Layer={Layer} Marker={Marker} />}
        {/* Calque « Visibilité » (groupe « Météo », exception accordée le 31/10/2026) : voile du camp par visibilité
            réduite, sous les graphiques ; rien par beau temps. Ses anneaux sont dans la source des unités. */}
        {mapSlug && <VisibilityLayers Source={Source} Layer={Layer} Marker={Marker} />}
        {mapSlug && <TacticalRoadsLayers Source={Source} Layer={Layer} Marker={Marker} />}
        {/* ZEF et ZEA de l'annexe A (OPO de division, exception à la carte figée du 14/10/2026) : sous le fuseau et
            les unités. */}
        {mapSlug && <FireZonesLayers Source={Source} Layer={Layer} Marker={Marker} />}
        {/* Groupe « Drones » (VAA, ROZ drones, NAI ; chantier « Drones », exception accordée à la carte figée) : sous le
            fuseau et les unités, décoché par défaut. */}
        {mapSlug && <DronesLayers Source={Source} Layer={Layer} Marker={Marker} zoom={zoom} />}
        {mapSlug && <AssistantRouteLayers Source={Source} Layer={Layer} Marker={Marker} />}
        {/* Niveau compagnie : rappel du GTIA et points de coordination, sous le fuseau du S/GTIA (docs/21 §4). */}
        {mapSlug && <CompanyLayers Source={Source} Layer={Layer} />}
        {mapSlug && <CorridorLayers Source={Source} Layer={Layer} Marker={Marker} zoom={zoom} bearing={bearing} />}
        {/* Lignes et zones de la ZO : au-dessus du fuseau et des LIMA, sous les unités. */}
        {mapSlug && (
          <ZoMeasuresLayers Source={Source} Layer={Layer} Marker={Marker} mapRef={mapRef} zoom={zoom} bearing={bearing} />
        )}
        {/* Annotations APP-6 (exception à la carte figée du 15/10/2026) : au-dessus des mesures, sous les unités. */}
        {mapSlug && <AnnotationsLayers Source={Source} Layer={Layer} />}
        {mapSlug && <UnitsLayers Source={Source} Layer={Layer} />}
        {mapSlug && <MissionSymbolsLayers Source={Source} Layer={Layer} mapRef={mapRef} />}
        {mapSlug && <PropagationLayers Source={Source} Layer={Layer} />}
        {/* Poignées du mode édition du fuseau : au-dessus des unités, sous l'aperçu d'un tracé. */}
        {mapSlug && <ManoeuvreLayers Source={Source} Layer={Layer} Marker={Marker} mapRef={mapRef} zoom={zoom} />}
        {/* Anneau, flèche et poignée du mode « Orienter la manœuvre » (docs/14 §2.14), pendant ce mode seulement. */}
        {mapSlug && <OrientationOverlay Source={Source} Layer={Layer} Marker={Marker} mapRef={mapRef} bearing={bearing} />}
        {mapSlug && draft && draft.family !== "annotation" && <DraftLayers Source={Source} Layer={Layer} />}
        {mapSlug && draft?.family === "annotation" && <AnnotationDraftLayer Source={Source} Layer={Layer} />}
        {/* Tracés et empreinte de la zone libre : montés quel que soit le camp (une zone se trace partout). */}
        <ZoneDrawLayers Source={Source} Layer={Layer} Marker={Marker} />

        <ScaleControl position="bottom-left" unit="metric" maxWidth={120} />
        {/* Attribution repliée derrière « i » (dépliée au toucher) : dépliée, elle occupait le coin bas droit et
            resserrait les barres basses, qui passaient sur deux lignes à 1440 px. */}
        <AttributionControl position="bottom-right" compact />
      </Map>

      {/* Tracé de zone au doigt : réticule du centre, que suit l'aperçu de l'axe ou de l'emprise. */}
      <Presence show={zoneTouchAim}>
        <Motion origin="fade" className="pointer-events-none absolute top-1/2 left-1/2 z-10 -translate-x-1/2 -translate-y-1/2 text-ink">
          <Crosshair size={24} aria-hidden />
        </Motion>
      </Presence>

      {/* Survol d'une unité : bulle d'information, anneau de portée ; mesure de distance et de temps. */}
      <UnitHoverOverlay mapRef={mapRef} />

      {/* Bandeaux en haut de la carte, un seul à la fois (R10) : tracé > manœuvre > analyse du terrain. Tous se
          rangent dans la bande que les panneaux flottants laissent libre (vue à gauche, fiche à droite), pour ne
          passer sous aucun d'eux, en disposition complète, compacte (consigne derrière « ? ») ou minimale selon
          sa largeur. Ils entrent et sortent en glissé ; un bandeau qui en remplace un autre se superpose à lui le
          temps de la transition (même case de la grille). */}
      <div
        className={clsx(
          "pointer-events-none absolute top-3 left-1/2 z-20 grid w-max max-w-[calc(100%-1.5rem)] -translate-x-1/2 justify-items-center",
          "*:col-start-1 *:row-start-1",
          "transition-[left,top,max-width] duration-(--gx-enter) ease-out motion-reduce:transition-none",
        )}
        // Dernière bande mesurée gardée après le retrait du bandeau : celui qui sort glisse vers le haut sur place,
        // sans revenir au centre en chemin.
        style={bannerBand ? { left: bannerBand.center, top: bannerBand.top, maxWidth: bannerBand.width } : undefined}
      >
        <Presence show={topBanner === "zone"}>
          {zoneDrawing && (
            <Motion origin="top" className="max-w-full">
              <ZoneDrawBanner
                layout={bannerBand?.layout ?? "full"}
                onCancel={() => useZoneDraw.getState().cancelDrawing()}
              />
            </Motion>
          )}
        </Presence>
        <Presence show={topBanner === "draft"}>
          {draft && (
            <Motion origin="top" className="max-w-full">
              <DraftBanner
                draft={draft}
                layout={bannerBand?.layout ?? "full"}
                onFinish={() => finishDraft(draft.points)}
                onCancel={() => setDraft(null)}
              />
            </Motion>
          )}
        </Presence>
        <Presence show={topBanner === "edit"}>
          <Motion origin="top" className="max-w-full">
            <ManoeuvreBanner
              layout={bannerBand?.layout ?? "full"}
              canEdit={gestures.canEdit}
              onRemove={gestures.removeSelected}
              onDone={() => void gestures.finish()}
            />
          </Motion>
        </Presence>
        <Presence show={topBanner === "orient"}>
          <Motion origin="top" className="max-w-full">
            <OrientationBanner layout={bannerBand?.layout ?? "full"} />
          </Motion>
        </Presence>
        {/* Analyse du terrain affichée hors de sa vue : rappel de ce que montrent les hachures, accès au rapport. Il
            se retire en douceur (entrée en édition du fuseau, tracé, analyse effacée), jamais d'un coup. */}
        <Presence show={topBanner === "terrain"}>
          {terrain && (
            <Motion
              origin="top"
              role="status"
              className="pointer-events-auto flex max-w-full items-center gap-1 rounded-xl border border-line bg-surface/95 py-1 pr-1 pl-3 shadow-float backdrop-blur"
            >
              <MountainSnow size={14} className="shrink-0 text-ink-soft" aria-hidden />
              <span className="mr-1 min-w-0 truncate text-ui font-medium text-ink">
                {terrain.camp ? t("Analyse du terrain du camp") : t("Analyse du terrain d'une zone")}
              </span>
              <Button variant="secondary" size="sm" onClick={() => setView("terrain")}>
                {t("Rapport")}
              </Button>
              <IconButton size="sm" onClick={() => setTerrain(null)} title={t("Effacer l'analyse")} aria-label={t("Effacer l'analyse")}>
                <X size={14} />
              </IconButton>
            </Motion>
          )}
        </Presence>
      </div>

      {/* Avis (déplacement enregistré, rôles recalculés…), juste au-dessus de la bande basse, au milieu de la bande
          libre entre les panneaux (il ne chevauche ni la barre d'état ni un panneau) ; au-dessus aussi des vues
          plein cadre (z-20 : ordre, DIREX, 3A, ordres reçus), qui les masquaient. */}
      <div
        className="pointer-events-none absolute left-1/2 z-30 w-max max-w-[calc(100%-1.5rem)] -translate-x-1/2"
        style={{ bottom: notice.bottom, ...(notice.band ? { left: notice.band.center, maxWidth: notice.band.width } : null) }}
      >
        <PropagationCard />
        <NoticeToast />
      </div>

      {/* Bande basse, libre de panneaux et des coins du fond de carte (échelle, logo, attribution) : temps de la
          manœuvre, puis relevé MGRS et zoom. Trop étroite pour les deux (tablette en portrait), elle passe sur deux
          lignes, les temps en bas, au lieu de déborder ; elle se range alors à côté des panneaux (bande libre)
          plutôt que sous eux, le relevé MGRS cédant la place si elle est trop étroite pour lui, le titre des temps
          aussi (jamais tronqué). */}
      <div
        ref={bottomRef}
        className={clsx(
          "pointer-events-none absolute bottom-3 left-1/2 z-10 flex w-max max-w-[calc(100%-1.5rem)] -translate-x-1/2 flex-wrap-reverse items-end justify-center gap-y-2",
          "transition-[left,max-width] duration-(--gx-enter) ease-out motion-reduce:transition-none",
        )}
        style={bottomBand ? { left: bottomBand.center, maxWidth: bottomBand.width } : undefined}
      >
        {/* Écart horizontal en marges internes (px-1 de chaque barre), pas en `gap` : Chrome ne compte pas le `gap`
            dans la largeur intrinsèque (`w-max`) d'un flex qui passe à la ligne, et les barres y passaient alors
            qu'elles tenaient sur une ligne. */}
        <div className="min-w-0 max-w-full px-1">
          <PhaseBar showTitle={phaseTitleFits(bottomBand ? bottomBand.width : Infinity)} />
        </div>
        <div className="min-w-0 max-w-full px-1">
          <MapStatusBar
            room={bottomBand ? bottomBand.width : Infinity}
            zoom={zoom}
            cursor={cursor}
            onZoomIn={() => mapRef.current?.zoomIn()}
            onZoomOut={() => mapRef.current?.zoomOut()}
            onFit={fitCamp}
            canFit={Boolean(mapDetail?.bbox ?? zoneBbox)}
            attribution={zoneAttribution}
            fitLabel={freeZone ? t("Cadrer sur le terrain") : t("Cadrer sur le camp")}
            history={!editing && !orienting}
            names={revealed && measureLevelOf(deployment) !== null}
            annotation={annotationBlock}
            onAnnotation={annotationButton}
            tip={tipVisible}
            onTipClose={() => useAnnotationTip.getState().dismiss()}
          />
        </div>
      </div>
    </div>
  );
}

function MapStatusBar(props: {
  /** Largeur disponible (px, bande libre ; Infinity : barre centrée, tout s'affiche) : le zoom, puis le relevé
   *  MGRS se masquent en deçà. */
  room: number;
  zoom: number;
  /** Coordonnée MGRS sous le curseur, « — » hors de la carte (dernière coordonnée estompée). */
  cursor: string;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFit: () => void;
  canFit: boolean;
  fitLabel: string;
  /** ↶ ↷ de l'historique (hors mode édition ; en édition, ils sont dans le bandeau Manœuvre). */
  history: boolean;
  /** Bouton des noms des lignes et des zones de la ZO (docs/15 §4.2) : seulement quand le dispositif en a. */
  names: boolean;
  /** Bouton « Annotation » (16/10/2026) : absent sans carte de dossier (`hidden`), bloqué pendant une opération en
   *  cours sur la carte (l'avis dit laquelle terminer), sinon il ouvre « Ajouter une annotation » au centre de la
   *  carte visible. */
  annotation: AnnotationMenuBlock;
  onAnnotation: (button: HTMLElement) => void;
  /** Bulle d'astuce affichée au-dessus du bouton (il pulse doucement pendant ce temps). */
  tip: boolean;
  onTipClose: () => void;
  /** Zone libre (licence ODbL, docs/14 §6.10) : mention des sources du serveur, en français (« » : la mention
   *  OpenStreetMap seule) ; null pour un camp. */
  attribution: string | null;
}) {
  const barRef = useRef<HTMLDivElement>(null);
  const mgrsRef = useRef<HTMLDivElement>(null);
  const zoomRef = useRef<HTMLDivElement>(null);
  // Largeurs mesurées (écart compris) quand l'élément est affiché ; repli : valeurs de la feuille de style.
  const widths = useRef({ mgrs: MGRS_FULL_PX + 9 + 2 * STATUS_GAP_PX, zoom: 62 });
  const [fit, setFit] = useState<StatusFit>(STATUS_ALL);
  const annotationRef = useRef<HTMLButtonElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  // Abscisse du milieu du bouton « Annotation » dans la barre : pointe de la bulle d'astuce.
  const [tipLeft, setTipLeft] = useState(0);
  const annotationShown = props.annotation !== "hidden";
  // Menu ouvert au moment d'appuyer sur le bouton : l'appui le referme (il ne le rouvre pas).
  const menuOpenAtPress = useRef(false);
  useLayoutEffect(() => {
    if (!props.tip) return;
    const button = annotationRef.current?.getBoundingClientRect();
    const root = rootRef.current?.getBoundingClientRect();
    if (button && root) setTipLeft(button.left + button.width / 2 - root.left);
  }, [props.tip]);
  const { room } = props;
  // Relevé stable : absent tant que le curseur n'est jamais venu sur la carte (pas de réserve vide après les
  // boutons) ; ensuite, il garde sa place et sa dernière coordonnée, estompée quand le curseur survole un panneau
  // ou quitte la carte — la barre ne change plus de largeur et les barres centrées ne se décalent plus.
  const onMap = props.cursor !== "—";
  const lastCursor = useLastDefined(onMap ? props.cursor : null);
  const shown = Boolean(lastCursor);
  // Disposition affichée, lue par la mesure sans en dépendre : l'effet ne se relance pas sur son propre résultat
  // (incident du 20/10/2026 : « Maximum update depth exceeded » en fermant une fiche — chaque disposition changeait
  // la largeur mesurée qui décidait de la suivante, et l'effet se relançait aussitôt, sans fin). Les variations de
  // la barre qui suivent une disposition passent par le ResizeObserver (une mesure par image) ; un aller-retour
  // entre deux dispositions est arrêté sur la plus compacte (`settleStatusFit`).
  const fitRef = useRef(fit);
  fitRef.current = fit;
  const recent = useRef<StatusFit[]>([]);
  useLayoutEffect(() => {
    const bar = barRef.current;
    if (!bar) return;
    recent.current = [];
    const measure = () => {
      const cache = widths.current;
      const current = fitRef.current;
      let optional = 0;
      const read = (el: HTMLElement | null, key: "mgrs" | "zoom", extra = 0) => {
        const w = el?.offsetWidth ?? 0;
        if (w <= 0) return;
        cache[key] = w + STATUS_GAP_PX + extra;
        optional += w + STATUS_GAP_PX;
      };
      // Relevé resserré : sa largeur complète est rétablie pour la mesure.
      read(mgrsRef.current, "mgrs", current.mgrs === "tight" ? MGRS_FULL_PX - MGRS_TIGHT_PX : 0);
      read(zoomRef.current, "zoom");
      // Écran étroit ou tactile, ou curseur jamais venu sur la carte : relevé masqué, il ne compte pas.
      let small = false;
      try {
        small = window.matchMedia("(max-width: 639.98px), (pointer: coarse)").matches;
      } catch {
        small = false;
      }
      // La mention OpenStreetMap d'une zone libre n'est plus dans la barre (conteneur de largeur nulle, demande
      // d'Alexis du 08/10/2026) : elle n'entre plus dans la mesure. Elle y comptait pour la largeur de son lien,
      // absente de la barre — un écart qui changeait avec la disposition et entretenait le va-et-vient.
      const next = statusFit(room, bar.offsetWidth - optional, { mgrs: small || !shown ? 0 : cache.mgrs, zoom: cache.zoom });
      const settled = settleStatusFit(current, next, recent.current);
      if (settled === current) return;
      recent.current = [...recent.current.slice(-3), current];
      fitRef.current = settled;
      setFit(settled);
    };
    measure();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    observer?.observe(bar);
    return () => observer?.disconnect();
  }, [room, shown]);
  return (
    <div ref={rootRef} className="relative">
      <AnnotationTip show={props.tip && annotationShown} left={tipLeft} onClose={props.onTipClose} />
      <div
        ref={barRef}
        className="pointer-events-auto flex h-10 items-center gap-1 rounded-xl border border-line bg-surface/95 px-1.5 shadow-float backdrop-blur"
      >
        <div
          className={clsx(
            "flex items-center gap-1 overflow-hidden transition-[max-width,opacity] duration-(--gx-enter) ease-out motion-reduce:transition-none",
            props.history ? "max-w-24 opacity-100" : "max-w-0 opacity-0",
          )}
          inert={!props.history}
        >
          <HistoryButtons size="sm" className="shrink-0" />
          <div className="mx-1 h-5 w-px shrink-0 bg-line" />
        </div>
        {/* Noms des lignes et des zones de la ZO (touche N) : la barre s'élargit en douceur, sa largeur mesurée entre
            dans le partage de la bande (`statusFit`). */}
        <div
          className={clsx(
            "flex items-center gap-1 overflow-hidden transition-[max-width,opacity] duration-(--gx-enter) ease-out motion-reduce:transition-none",
            props.names ? "max-w-12 opacity-100" : "max-w-0 opacity-0",
          )}
          inert={!props.names}
        >
          <MeasureNamesToggle size="sm" />
          <div className="mx-1 h-5 w-px shrink-0 bg-line" />
        </div>
        {/* « Annotation » : la barre s'élargit en douceur quand la carte du dossier paraît. Sans `overflow-hidden` une
            fois affiché : la pulsation du bouton (bulle d'astuce) n'est pas rognée. */}
        <div
          className={clsx(
            "flex items-center gap-1 transition-[max-width,opacity] duration-(--gx-enter) ease-out motion-reduce:transition-none",
            annotationShown ? "max-w-12 opacity-100" : "max-w-0 overflow-hidden opacity-0",
          )}
          inert={!annotationShown}
        >
          <IconButton
            ref={annotationRef}
            size="sm"
            data-attention="map-annotation"
            className={clsx("shrink-0", props.tip && "wayfinding")}
            blocked={props.annotation !== null && annotationShown}
            onBlocked={() => props.onAnnotation(annotationRef.current!)}
            onPointerDown={() => {
              menuOpenAtPress.current = useAnnotationMenu.getState().request !== null;
            }}
            onClick={(e) => {
              if (menuOpenAtPress.current) {
                menuOpenAtPress.current = false;
                useAnnotationMenu.getState().close();
                return;
              }
              props.onAnnotation(e.currentTarget);
            }}
            title={annotationButtonTitle(props.annotation)}
            aria-label={t("Ajouter une annotation")}
            aria-haspopup="menu"
          >
            <MapPinPlus size={14} />
          </IconButton>
        </div>
        <SupportsToggle />
        <div className="mx-1 h-5 w-px shrink-0 bg-line" />
        <IconButton size="sm" onClick={props.onZoomOut} title={t("Dézoomer")} aria-label={t("Dézoomer")}>
          <Minus size={14} />
        </IconButton>
        <IconButton size="sm" onClick={props.onZoomIn} title={t("Zoomer")} aria-label={t("Zoomer")}>
          <Plus size={14} />
        </IconButton>
        <IconButton
          size="sm"
          data-attention="map-fit"
          onClick={props.onFit}
          disabled={!props.canFit}
          title={props.fitLabel}
          aria-label={props.fitLabel}
        >
          <Crosshair size={14} />
        </IconButton>
        {/* Écran étroit ou tactile, curseur jamais venu sur la carte : le relevé MGRS cède la place. */}
        <div
          ref={mgrsRef}
          className={clsx(
            "flex items-center gap-1 max-sm:hidden pointer-coarse:hidden",
            (fit.mgrs === "hidden" || !shown) && "hidden",
          )}
        >
          <div className="mx-1 h-5 w-px bg-line" />
          <div
            className={clsx(
              "px-1.5 font-mono text-ui whitespace-nowrap tabular-nums text-ink-soft",
              "transition-opacity duration-(--gx-enter) ease-out motion-reduce:transition-none",
              fit.mgrs === "tight" ? "min-w-[146px]" : "min-w-[178px]",
              !onMap && "opacity-40",
            )}
            title={t("Coordonnée MGRS sous le curseur")}
          >
            {lastCursor}
          </div>
        </div>
        {/* Niveau de zoom et mention des sources retirés du bandeau (demande d'Alexis du 08/10/2026) : les crédits
            sont dans l'aide (Crédits et licences). */}
        <div ref={zoomRef} className="hidden">
          <div className="mx-1 h-5 w-px bg-line" />
          <div className="px-1.5 font-mono text-ui text-ink-muted" title={t("Niveau de zoom")}>
            z{props.zoom.toFixed(1)}
          </div>
        </div>
        {/* Zone libre : mention OpenStreetMap (ODbL) toujours visible ; la source du relief est dans l'info-bulle. */}
        <div
          className={clsx(
            "flex items-center gap-1 overflow-hidden transition-[max-width,opacity] duration-(--gx-enter) ease-out motion-reduce:transition-none",
            "max-w-0 opacity-0",
          )}
          inert
        >
          <div className="mx-1 h-5 w-px shrink-0 bg-line" />
          {/* Bande étroite ou doigt : forme courte « © OSM », le texte complet dans l'info-bulle et le lien. */}
          <a
            href="https://www.openstreetmap.org/copyright"
            target="_blank"
            rel="noreferrer"
            title={props.attribution ? tServer(props.attribution) : t("© contributeurs OpenStreetMap (ODbL)")}
            aria-label={t("© contributeurs OpenStreetMap (ODbL)")}
            className="rounded-md px-1.5 text-meta whitespace-nowrap text-ink-muted transition-colors hover:text-ink"
          >
            {fit.osm === "short" ? t("© OSM") : t("© contributeurs OpenStreetMap")}
          </a>
        </div>
      </div>
    </div>
  );
}

/** Bascule des unités d'appui et de soutien (10/10/2026), à portée de main dans le bandeau de la carte : la carte
 *  tactique ne montre que la mêlée par défaut ; la même bascule est dans les Calques. */
function SupportsToggle() {
  const shown = useUi((s) => s.supportUnitsVisible);
  const setShown = useUi((s) => s.setSupportUnitsVisible);
  const label = shown ? t("Masquer les appuis et le soutien") : t("Afficher les appuis et le soutien");
  return (
    <IconButton
      size="sm"
      onClick={() => setShown(!shown)}
      aria-pressed={shown}
      title={label}
      aria-label={label}
      active={shown}
    >
      <Layers2 size={14} />
    </IconButton>
  );
}
